// theme-search-spring-plp.js
// Loads on collection and search templates only.
// Depends on: SearchSpringCommon, getSiteId, fetchSearchSpringResults,
//             searchProducts, isPriceFacetField (theme-search-spring.js)

// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring PLP & Search: Configuration
// ─────────────────────────────────────────────────────────────────────────────

/** Refs that provide the English "Price" label (UK uses ss_price; meta may be market-scoped). */
const ENGLISH_PRICE_LABEL_REFS = ['price', 'ss_price', 'en_price'];

// Country/language codes we use as facet prefixes (en_, es_, de_, etc.)
const FACET_PREFIXES = ['en_', 'ie_', 'nl_', 'es_', 'fr_', 'it_', 'pt_', 'de_', 'at_', 'dk_', 'be_', 'fi_', 'im_', 'pl_'];

/** Facet prefix from path (/es-es/ -> es) or market handle (es-es -> es). All markets use xx-yy. */
function getFacetCountryCodeForRequest() {
    const pathCode = getFacetCountryCodeFromPath();
    if (pathCode) return pathCode;
    const market = window.themeVariables?.settings?.currentMarket;
    if (typeof market === 'string' && market.includes('-')) {
        const prefix = market.split('-')[0];
        if (prefix.length === 2) return prefix;
    }
    return 'en';
}

/** Derive country code from path (e.g. /es-es/ -> es). Use when themeVariables is wrong or missing. */
function getFacetCountryCodeFromPath() {
    const match = typeof window !== 'undefined' && window.location.pathname.match(/^\/([a-z]{2})-([a-z]{2})(\/|$)/);
    return match ? match[1] : null;
}

function hasFacetPrefix(ref) {
    return FACET_PREFIXES.some(p => ref.startsWith(p));
}

/** Returns market prefix (e.g. en_, pt_) if ref is market-specific, else null. Handles both en_colour and ss_en_colour. */
function getMarketPrefixFromRef(ref) {
    if (ref.startsWith('ss_')) {
        const rest = ref.slice(3);
        for (const p of FACET_PREFIXES) {
            if (rest.startsWith(p)) return p;
        }
        return null;
    }
    for (const p of FACET_PREFIXES) {
        if (ref.startsWith(p)) return p;
    }
    return null;
}

const SEARCHSPRING_FACET_LIMIT = 25;

/**
 * Build includedFacets for the current market from searchspring_filter_item metaobjects (data-ss-meta-filters).
 * Only the display set (one prefix) is requested so we stay under the 25-facet API cap.
 * Returns null if no meta container or empty (API returns default facets).
 */
function getIncludedFacetsFromMetaFilters() {
    const facetLanguage = window.themeVariables?.settings?.currentLanguage;
    const facetCountryCode = getFacetCountryCodeForRequest();
    const currentMarket = window.themeVariables?.settings?.currentMarket || 'gb';

    const defaultToEnglish = (facetCountryCode !== 'en' && facetLanguage === 'en');
    const displayPrefix = defaultToEnglish ? 'en_' : (facetCountryCode + '_');

    const container = document.querySelector('[data-ss-meta-filters]');
    const metaDivs = container ? container.querySelectorAll(':scope > div[data-reference]') : [];

    if (metaDivs.length === 0) return null;

    const included = new Set(['ss_price', 'variant_size']);
    const marketPriceField = SearchSpringCommon.getPriceFieldForMarket(currentMarket);
    included.add(marketPriceField);
    metaDivs.forEach(div => {
        const ref = (div.getAttribute('data-reference') || '').trim();
        if (!ref) return;
        if (ref === 'variant_size' || ref === 'ss_price') {
            included.add(ref);
        } else if (ref.endsWith('_price')) {
            const marketPrefix = getMarketPrefixFromRef(ref);
            if (marketPrefix === null) included.add(ref);
            else if (marketPrefix === displayPrefix) included.add(ref);
        } else {
            const marketPrefix = getMarketPrefixFromRef(ref);
            if (marketPrefix === null) {
                included.add(ref);
            } else if (marketPrefix === displayPrefix) {
                if (ref === displayPrefix + 'size') return;
                included.add(ref);
            }
        }
    });
    const arr = Array.from(included);
    return arr.length > SEARCHSPRING_FACET_LIMIT ? arr.slice(0, SEARCHSPRING_FACET_LIMIT) : arr;
}

function buildFilterParams(filters = {}) {
    const params = {};
    
    Object.entries(filters).forEach(([field, values]) => {
      const valuesArray = Array.isArray(values) ? values : [values];
      if (valuesArray.length > 0) {
        // Use filter. prefix since that's what works in your URL
        params[`filter.${field}`] = valuesArray[0];
      }
    });
    

    
    return params;
  }

// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring Rendering Functions
// ─────────────────────────────────────────────────────────────────────────────

function renderProductItem(ssProduct, lazyload = true, language = 'en', countryCode = 'gb', shopifyPrice = null, usePlaceholder = false) {

    // Accept only SearchSpring product data
    const ss = ssProduct;
    const t = window.searchSpringTranslations || {};

    const productUrl = `${window.Shopify.routes.root}products/${ss.handle}`;

    // Assign displayTitle as the default title from SS, preferring a localized title if available
    let displayTitle = SearchSpringCommon.getLocalizedTitle(ss, ss.title || '', language);

    // Decode HTML entities/encoding issues and strip colours from the title if configured
    displayTitle = SearchSpringCommon.getDisplayTitle(displayTitle);

    const pricing = SearchSpringCommon.resolvePricing(ss, shopifyPrice, countryCode);
    const imageUrl = ss.thumbnailImageUrl || ss.image || '';
    const secondaryImageEnabled = window.themeVariables?.settings?.secondaryProductImageEnabled;
    const secondaryImage = ss.images && ss.images.length > 1 ? ss.images.split('|')[1] : null;
    const available = ss.available !== undefined ? ss.available : true;
    const vendor = ss.vendor || '';
    const id = ss.id || ss.sku || ss.handle;

    const productLabelHTML = SearchSpringCommon.buildProductLabelHTML(ss.tags || [], available, pricing, t);
    const priceHTML = SearchSpringCommon.buildPriceHTML(pricing, t, { usePlaceholder });

    const variants = ss.variants || [];
    const drawerId = `product-${id}-drawer`;
    const popoverId = `product-${id}-popover`;
    const ctaHTML = SearchSpringCommon.buildCtaHTML({ available, variants, productId: id, drawerId, popoverId, t });

    return `
        <product-item class="product-item fade-in${!available ? ' product-item--sold-out' : ''}" data-product-id="${id}" data-handle="${ss.handle}" data-parent-id="${ss.uid || ss.id}" data-uid="${ss.uid || ss.id}" data-sku="${ss.sku || ''}" data-ss-id="${ss.ss_id || ''}">
        <div class="product-item__image-wrapper ${secondaryImageEnabled && secondaryImage ? ' product-item__image-wrapper--multiple' : ''}">
            <div class="product-item__label-list label-list">
            ${productLabelHTML}
            </div>
            <a href="${productUrl}" class="product-item__aspect-ratio aspect-ratio" style="--aspect-ratio: ${SearchSpringCommon.PRODUCT_IMAGE_ASPECT_RATIO}" data-ss-beacon="product-link">
              <img class="product-item__primary-image" src="${imageUrl}" alt="${displayTitle}" ${lazyload ? 'loading="lazy"' : ''}>
              ${secondaryImageEnabled && secondaryImage ? `<img class="product-item__secondary-image" src="${secondaryImage}" ${lazyload ? 'loading="lazy"' : ''}>` : ''}
            </a>
            ${ctaHTML}
        </div>
        <div class="product-item__info">
          <div class="product-item-meta">
            ${(window.themeVariables?.settings?.showVendor && vendor) ? `<span class="product-item-meta__vendor">${vendor}</span>` : ''}
            <a href="${productUrl}" class="product-item-meta__title" data-ss-beacon="product-link">${displayTitle}</a>
            <div class="product-item-meta__price-list-container">
              <div class="price-list price-list--centered">
                ${priceHTML}
              </div>
            </div>
          </div>
        </div>
        </product-item>
        <quick-buy-popover
            id="${popoverId}"
            href="${productUrl}?view=quick-buy-popover"
            class="popover popover--quick-buy hidden-tablet-and-up">
        </quick-buy-popover>
        <quick-buy-drawer
            id="${drawerId}"
            href="${productUrl}?view=quick-buy-drawer"
            class="drawer drawer--large drawer--quick-buy hidden-phone">
        </quick-buy-drawer>
    `;
}

function buildPreloadLinks(products) {
    const head = document.getElementsByTagName('head')[0];
    products.forEach(product => {
        const imageUrl = product.thumbnailImageUrl || product.image || null;
        if (imageUrl) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = imageUrl;
            head.appendChild(link);
        }

        const secondaryImage = product.images && product.images.length > 1 ? product.images.split('|')[1] : null;
        const secondaryImageEnabled = window.themeVariables?.settings?.secondaryProductImageEnabled
        if (secondaryImageEnabled && secondaryImage) {
            const secondaryImageLink = document.createElement('link');
            secondaryImageLink.rel = 'preload';
            secondaryImageLink.as = 'image';
            secondaryImageLink.href = secondaryImage;
            head.appendChild(secondaryImageLink);
        }
    });
}


function patchProductPrice(container, handle, shopifyPrice) {
    const card = container?.querySelector(`product-item[data-handle="${handle}"]`);
    if (!card) return;
    const priceList = card.querySelector('.price-list');
    if (!priceList) return;

    const t = window.searchSpringTranslations || {};
    const pricing = SearchSpringCommon.resolvePricing({}, shopifyPrice);
    priceList.innerHTML = SearchSpringCommon.buildPriceHTML(pricing, t);
}


function sanitizeBannerMarkup(rawHtml) {
    if (!rawHtml || typeof rawHtml !== 'string') return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = rawHtml;

    const scripts = tempDiv.querySelectorAll('script');
    if (scripts) {
        scripts.forEach(script => script.remove());
    }

    const images = tempDiv.querySelectorAll('img');
    if (images) {
        images.forEach(img => img.setAttribute('loading', 'lazy'));
    }

    const iframe = tempDiv.querySelector('iframe[src*="youtube.com/watch"]');
    if (iframe) {
        try {
            const url = new URL(iframe.src);
            const videoId = url.searchParams.get('v');
            if (videoId) iframe.src = `https://www.youtube.com/embed/${videoId}`;
        } catch (e) {
            console.error('Could not parse YouTube URL for inline banner', e);
        }
    }

    return tempDiv.innerHTML;
}

function getInlineMerchandisingPlacements(merchandisingContent) {
    if (!merchandisingContent) return [];

    const inlineSource = merchandisingContent.inline
        || merchandisingContent.inlineBanners
        || merchandisingContent.inline_banners
        || [];

    const placements = [];

    const parsePosition = (value) => {
        if (value === null || typeof value === 'undefined') return null;
        if (typeof value === 'object') {
            return parsePosition(
                value.index
                ?? value.position
                ?? value.after
                ?? value.slot
                ?? value.value
            );
        }
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const direct = Number.parseInt(trimmed, 10);
            if (Number.isFinite(direct)) return direct;
            const matched = trimmed.match(/(\d+)/);
            if (matched) {
                const extracted = Number.parseInt(matched[1], 10);
                return Number.isFinite(extracted) ? extracted : null;
            }
        }
        return null;
    };

    const getCandidatePositions = (item) => {
        if (!item || typeof item !== 'object') return [];
        return [
            item.position,
            item.index,
            item.slot,
            item.after,
            item.insertAfter,
            item.product,
            item.afterProduct,
            item.after_product,
            item.productPosition,
            item.product_position,
            item.row,
            item.config?.position,
            item.config?.index,
            item.config?.after,
            item.config?.afterProduct,
            item.config?.after_product,
            item.settings?.position,
            item.settings?.index,
            item.settings?.after,
            item.data?.position,
            item.data?.index,
            item.merchandising?.position,
            item.merchandising?.index,
            item.attributes?.position,
            item.attributes?.index
        ];
    };

    const pushPlacement = (position, html) => {
        const cleaned = sanitizeBannerMarkup(html);
        if (!cleaned) return;
        const parsedPosition = parsePosition(position);
        placements.push({
            position: parsedPosition,
            html: cleaned
        });
    };

    if (Array.isArray(inlineSource)) {
        inlineSource.forEach(item => {
            if (typeof item === 'string') {
                pushPlacement(null, item);
                return;
            }
            if (!item || typeof item !== 'object') return;
            const html = item.html || item.content || item.value || item.banner || item.markup || '';
            const position = getCandidatePositions(item).find(v => parsePosition(v) !== null);
            pushPlacement(position, html);
        });
    } else if (typeof inlineSource === 'object') {
        Object.entries(inlineSource).forEach(([position, value]) => {
            if (typeof value === 'string') {
                pushPlacement(position, value);
                return;
            }
            if (!value || typeof value !== 'object') return;
            const html = value.html || value.content || value.value || value.banner || value.markup || '';
            const nestedPosition = getCandidatePositions(value).find(v => parsePosition(v) !== null);
            pushPlacement(nestedPosition ?? position, html);
        });
    }

    return placements.sort((a, b) => {
        if (a.position === null && b.position === null) return 0;
        if (a.position === null) return 1;
        if (b.position === null) return -1;
        return a.position - b.position;
    });
}

function renderInlineBannerItem(html, index) {
    return `
        <div class="searchspring-inline-banner" data-searchspring-inline-banner="${index}">
            ${html}
        </div>
    `;
}

async function renderSearchSpringProductCards(searchSpringData, containerSelector = '.search-spring-results-container') {
    // Cache container element for repeated use
    let container = renderSearchSpringProductCards._containerCache?.[containerSelector];
    if (!container) {
        container = document.querySelector(containerSelector);
        if (!renderSearchSpringProductCards._containerCache) renderSearchSpringProductCards._containerCache = {};
        renderSearchSpringProductCards._containerCache[containerSelector] = container;
    }
    if (!container || !searchSpringData?.results?.length) {
        if (container) {
            const t = window.searchSpringTranslations || {};
            const noResults = t.noResults || 'No results could be found.';
            container.innerHTML = `
                <div class="empty-state empty-state--bottom-only">
                    <p class="text--large">${noResults}</p>
                </div>
            `;
            const productFacet = container.closest('.product-facet');
            productFacet?.classList.add('search-empty-results');
            document.body.classList.add('search-empty-results');
        }
        return;
    }

    const productFacet = container?.closest('.product-facet');
    productFacet?.classList.remove('search-empty-results');
    document.body.classList.remove('search-empty-results');

    const productsPerPage = getSectionProductsPerPage();
    const productResults = searchSpringData.results.slice(0, productsPerPage);

    //Grab language and market settings, and determine country code for rendering
    const renderLanguage = window.themeVariables?.settings?.currentLanguage;
    const renderMarket = window.themeVariables?.settings?.currentMarket;
    const renderMarketCodeMap = { 'gb': 'gb', 'ie': 'ie', 'nether': 'nl', 'spain': 'es', 'france': 'fr', 'italy': 'it', 'portugal': 'pt', 'germany': 'de', 'austria': 'at', 'denmark': 'dk', 'belgium':'be', 'finland': 'fi', 'isle-of-man': 'im', 'poland': 'pl' };

    // Default to 'gb' if market code is not recognized
    const renderCountryCode = renderMarketCodeMap[renderMarket] || 'gb';

    const inlinePlacements = getInlineMerchandisingPlacements(searchSpringData?.merchandising?.content);
    const indexedInlinePlacements = inlinePlacements.reduce((acc, placement, index) => {
        const key = placement.position === null ? 'end' : String(placement.position);
        if (!acc[key]) acc[key] = [];
        acc[key].push(renderInlineBannerItem(placement.html, index));
        return acc;
    }, {});

    const usePlaceholders = window.themeVariables?.settings?.pricePlaceholderEnabled !== false;
    const handles = productResults.map((p) => p.handle).filter(Boolean);

    if (usePlaceholders) {
        // Phase 1: render cards immediately with price placeholders to avoid layout shift
        let cardHTML = '';
        productResults.forEach((ssProduct, index) => {
            cardHTML += renderProductItem(ssProduct, index > 4, renderLanguage, renderCountryCode, null, true);
            const afterPosition = String(index + 1);
            if (indexedInlinePlacements[afterPosition]?.length) {
                cardHTML += indexedInlinePlacements[afterPosition].join('');
            }
        });
        if (indexedInlinePlacements.end?.length) {
            cardHTML += indexedInlinePlacements.end.join('');
        }
        if (container && typeof container.innerHTML !== 'undefined') {
            container.innerHTML = cardHTML;
            container.dispatchEvent(new CustomEvent('searchspring:rendered', {
                bubbles: true,
                detail: {
                    responseId: searchSpringData.pagination?.responseId || searchSpringData.responseId || '',
                    contextType: window.themeVariables?.settings?.pageType === 'collection' ? 'category' : 'search'
                }
            }));
        }

        // Phase 2: fetch Shopify prices (Global-e controlled) and patch into rendered cards
        SearchSpringCommon.fetchShopifyPricesForHandles(handles).then((shopifyPrices) => {
            productResults.forEach((ssProduct) => {
                if (!ssProduct.handle) return;
                const shopifyPrice = shopifyPrices.get(ssProduct.handle);
                if (!shopifyPrice) return;
                patchProductPrice(container, ssProduct.handle, shopifyPrice);
            });
        });
    } else {
        const shopifyPrices = await SearchSpringCommon.fetchShopifyPricesForHandles(handles);
        let cardHTML = '';
        productResults.forEach((ssProduct, index) => {
            const shopifyPrice = ssProduct.handle ? shopifyPrices.get(ssProduct.handle) ?? null : null;
            cardHTML += renderProductItem(ssProduct, index > 4, renderLanguage, renderCountryCode, shopifyPrice);
            const afterPosition = String(index + 1);
            if (indexedInlinePlacements[afterPosition]?.length) {
                cardHTML += indexedInlinePlacements[afterPosition].join('');
            }
        });
        if (indexedInlinePlacements.end?.length) {
            cardHTML += indexedInlinePlacements.end.join('');
        }
        if (container && typeof container.innerHTML !== 'undefined') {
            container.innerHTML = cardHTML;
            container.dispatchEvent(new CustomEvent('searchspring:rendered', {
                bubbles: true,
                detail: {
                    responseId: searchSpringData.pagination?.responseId || searchSpringData.responseId || '',
                    contextType: window.themeVariables?.settings?.pageType === 'collection' ? 'category' : 'search'
                }
            }));
        }
    }
}

async function renderTrendingQueries(trendingQueries, containerSelector = '.ss_predictive-search__menu-list') {

  const container = document.querySelector(containerSelector);

  if (!container || !trendingQueries?.length) { 
    return;
  }

  const marketPrefixMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})/);
  const marketPrefix = marketPrefixMatch ? marketPrefixMatch[0] : '';

  const listItemsHTML = trendingQueries.map(item => {
    return `<li class="linklist__item"><a href="${marketPrefix}/search?q=${encodeURIComponent(item.searchQuery)}" class="link--faded">${item.searchQuery}</a></li>`;
  }).join('');

  let suggestionsHTML = `
  <div class="predictive-search__menu">
    <p class="predictive-search__menu-title heading heading--small">${window.searchSpringTranslations.trending}</p>
    <ul class="linklist list--unstyled" role="list">${listItemsHTML}</ul>
  </div>`;

  if (container && typeof container.innerHTML !== 'undefined') {
      container.innerHTML = suggestionsHTML + container.innerHTML;
  }

}

async function renderSearchSpringBanners(merchandisingContent) {
    // Defer banner rendering for better page load performance
    const runBannerRender = () => {
        const bannerPositions = ['header', 'banner', 'footer', 'left'];

        bannerPositions.forEach(position => {
            const containerId = `searchspring-${position}-banners`;
            const bannerContainer = document.getElementById(containerId);

            if (!bannerContainer) {
                return;
            }

            if (bannerContainer && typeof bannerContainer.innerHTML !== 'undefined') {
                bannerContainer.innerHTML = '';
            }

            if (merchandisingContent && merchandisingContent[position] && merchandisingContent[position].length > 0) {
                let finalHTML = '';

                merchandisingContent[position].forEach(bannerString => {
                    finalHTML += sanitizeBannerMarkup(bannerString || '');
                });

                if (finalHTML && bannerContainer && typeof bannerContainer.innerHTML !== 'undefined') {
                    bannerContainer.innerHTML = finalHTML;
                }
            }
        });
    };

    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(runBannerRender);
    } else {
        setTimeout(runBannerRender, 0);
    }
}
  

// Called when the search is performed, filters are changed, or when the pagination is updated
async function renderSearchSpring(page) {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q')?.trim();
    if (!query) return;

    // Collect filter parameters from URL
    const filters = {};
    for (const [key, value] of params.entries()) {
        const lowMatch = key.match(/^filter\.(.+)\.low$/);
        if (lowMatch && isPriceFacetField(lowMatch[1])) {
            const field = lowMatch[1];
            const highValue = params.get(`filter.${field}.high`);
            if (highValue) {
                if (!filters[field]) filters[field] = [];
                filters[field].push(`${value}-${highValue}`);
            }
        } else if (/^filter\.[^.]+\.high$/.test(key)) {
            // Skip - handled with low
        } else if (key.startsWith('filter.') && !/^filter\.[^.]+\.(low|high)$/.test(key)) {
            const fieldName = key.replace('filter.', '').split('.')[0];
            if (!filters[fieldName]) filters[fieldName] = [];
            filters[fieldName].push(value);
        }
    }

    try {
        const results = await searchProducts(getSiteId(), query, {
            page: page,
            resultsPerPage: getSectionProductsPerPage(),
            filters: Object.keys(filters).length > 0 ? filters : undefined
        });
        await renderSearchSpringProductCards(results, '.search-spring-results-container');
    } catch (err) {
        console.error('SearchSpring: renderSearchSpring failed', err);
    }
}

/**
 * Search products for collection pages using bgfilter parameter
 */
function searchCollectionProducts(siteId, options = {}) {
    return fetchSearchSpringResults(siteId, { ...options, type: 'category' });
}

// Make functions available globally
window.searchCollectionProducts = searchCollectionProducts;

function getSectionProductsPerPage() {
    const section = document.querySelector('[data-products-per-page]');
    if (section) {
        return parseInt(section.dataset.productsPerPage);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring Facets Integration
// ─────────────────────────────────────────────────────────────────────────────

class SearchSpringFacets {
    constructor() {
      this.currentFilters = new Map();
      this.currentQuery = '';
      this.currentPage = 1;
      this.currentSort = { field: 'relevance' };
      this.facetsData = null;
      this.filtersContainer = null;
      this.initialPriceRange = null;
      this.initialPriceRangeMap = {};
      this.productsPerPage = getSectionProductsPerPage(); 

      this.translations = window.searchSpringTranslations || {};

      this.cachedElements = {
        resultsCount: null,
        pagination: null,
        drawerCounter: null,
        metaBarCounter: null,
        mainFilterButton: null,
        mobileFiltersButton: null,
        clearFiltersLink: null,
        resultsContainer: null,
        sortContainer: null
      };
    }

    cacheElements() {
        this.cachedElements = {
            resultsCount: document.querySelector('.product-facet__meta-bar-item--count'),
            pagination: document.querySelector('.pagination'),
            mobileFiltersButton: document.querySelector('.mobile-toolbar__item--filters'),
            resultsContainer: document.querySelector('.search-spring-results-container'),
            sortContainer: document.querySelector('.product-facet__meta-bar-item--sort')
        };   
    }
  
    // Initialize facets on search page
    async init() {
        this.setupScrollPersistence();

        const urlParams = new URLSearchParams(window.location.search);
        this.currentQuery = urlParams.get('q') || '';
      
        if (!this.currentQuery) return;

        this.renderSkeletonCards(this.productsPerPage);

        await this.checkForRedirect(this.currentQuery);

        this.filtersContainer = document.getElementsByClassName('searchspring-facet-filters')[0];
        if (!this.filtersContainer) {
            return;
        }
  
        this.setupFiltersStructure();

        // Parse existing filters from URL FIRST
        this.parseUrlFilters();

        this.includedFacets = getIncludedFacetsFromMetaFilters();

        // Use your existing renderSearchSpring function but capture facets
        await this.loadSearchWithFacets();
    }

    setupFiltersStructure() {
        if (!this.filtersContainer) {
            return;
        }
        
        const isDrawer = this.filtersContainer.classList.contains('drawer');
        const translations = this.translations;

        const onSearchPage = window.location.pathname.includes('/search');
        
        this.filtersContainer.innerHTML = `
            <span class="drawer__overlay"></span>
            
            ${isDrawer ? `
                <header class="drawer__header">
                    <p class="drawer__title heading h6">${translations.filters}
                        <span class="product-facet__active-count bubble-count" style="display: none;">0</span>
                    </p>
                    <a class="drawer__header-action link text--subdued" style="display: none;" data-action="clear-all-filters" href="#">${translations.clearFilters}</a>
                    ${!onSearchPage ? `
                    <button type="button" class="drawer__close-button tap-area" data-action="close" title="${translations.closeAccessibility}">
                        <svg focusable="false" width="16" height="16" class="icon icon--close" viewBox="0 0 16 16">
                            <path d="M15 1L1 15M1 1l14 14" stroke="currentColor" stroke-width="1.4" fill="none"></path>
                        </svg>
                    </button>
                ` : ''}
                </header>
            ` : ''}
            
            <div class="drawer__content">
                <form id="searchspring-facet-filters-form">
                    <input type="hidden" name="sort_by" value="${this.currentSort}">
                    <input type="hidden" name="q" value="${this.currentQuery}">
                    <input type="hidden" name="type" value="product">
                    
                    <div class="product-facet__active-list tag-list hidden-phone"></div>
                    <div class="product-facet__filter-list">
                        <p>${translations.loadingFilters}</p>
                    </div>
                </form>
            </div>
            
            ${isDrawer ? `
                <div class="drawer__footer drawer__footer--no-top-padding">
                    <button type="button" class="button button--primary button--full" data-action="close">${translations.viewResults}</button>
                </div>
            ` : ''}
        `;
    }
  
    // Parse filters from current URL
    parseUrlFilters() {
      const urlParams = new URLSearchParams(window.location.search);
      
      this.currentPage = parseInt(urlParams.get('page')) || 1;

      this.currentSort = this.getSearchSpringSortFromUrl();

      for (const [key, value] of urlParams.entries()) {
        const lowMatch = key.match(/^filter\.(.+)\.low$/);
        if (lowMatch && isPriceFacetField(lowMatch[1])) {
          const field = lowMatch[1];
          const high = urlParams.get(`filter.${field}.high`);
          if (high) {
            this.currentFilters.set(field, [`${value}-${high}`]);
          }
        }
      }

      // Parse SearchSpring filters (filter.fieldName format) - skip filter.X.low/high (handled above)
      for (const [key, value] of urlParams.entries()) {
        if (key.startsWith('filter.') && !/^filter\.[^.]+\.(low|high)$/.test(key)) {
          const fieldName = key.replace('filter.', '').split('.')[0];
          if (!this.currentFilters.has(fieldName)) {
            this.currentFilters.set(fieldName, []);
          }
          this.currentFilters.get(fieldName).push(value);
        }
      }
      
      // Parse page and sort
      this.currentPage = parseInt(urlParams.get('page')) || 1;
    }
  
    // Use existing search but extract facets
    async loadSearchWithFacets() {
        try {
            // Initial call WITHOUT sort parameter. Uses this.currentPage (parsed from the URL)
            // rather than a hardcoded 1 so a page-only URL (e.g. returning via the back button
            // to ?page=3) renders the correct page instead of always resetting to page 1.
            const initialResults = await searchProducts(getSiteId(), this.currentQuery, {
                page: this.currentPage,
                resultsPerPage: this.productsPerPage,
                ...(this.includedFacets && this.includedFacets.length ? { includedFacets: this.includedFacets } : {})
            });

            this.updateTabsNavigation(initialResults);

            if (initialResults.merchandising && initialResults.merchandising.content) {
                renderSearchSpringBanners(initialResults.merchandising.content);
            }

            if (initialResults.facets) {
                this.facetsData = initialResults.facets;
                // Store initial price range for each price facet
                this.facetsData.filter(f => isPriceFacetField(f.field) && f.range?.length >= 2).forEach(priceFacet => {
                    this.initialPriceRangeMap[priceFacet.field] = {
                        min: Math.floor(priceFacet.range[0]),
                        max: Math.ceil(priceFacet.range[1])
                    };
                    if (priceFacet.field === 'ss_price') this.initialPriceRange = this.initialPriceRangeMap[priceFacet.field];
                });
                this.renderFacets(); // Render facets based on initial load
            }

            this.cacheElements(); // Cache elements

            if (initialResults.sorting) {
                // Render the sort dropdown based on options from the initial load
                this.renderSortDropdown(initialResults.sorting);
            }

            this.initialSearchResults = initialResults; // Store initial results

            // Handle existing URL parameters
            if (this.currentFilters.size > 0 || (this.currentSort.field !== 'relevance')) {
                 // If filters/sort WERE in the URL, call searchWithFilters
                 await this.searchWithFilters(this.currentPage); // Pass page from URL
            } else {
                 // If the URL was clean, render the initial results.
                 await renderSearchSpringProductCards(initialResults, '.search-spring-results-container');
                 if (initialResults.pagination) {
                     this.renderPagination(initialResults.pagination);
                 }
                 this.updateResultsCount(initialResults);
            }

            this.restoreScrollPositionIfNeeded();

        } catch (error) {
            console.error('SearchSpring: loadSearchWithFacets failed', error);
        }
    }


    // Update tabs navigation with initial search count
    updateTabsNavigation(searchResults) {
        const tabsContainer = document.getElementById('searchspring-tabs-nav');
        if (!tabsContainer) {
            return;
        }

        const totalCount = searchResults.pagination?.totalResults || searchResults.results?.length || 0;
        const tabButton = tabsContainer.querySelector('.tabs-nav__item');
        
        if (tabButton) {
            tabButton.textContent = `${window.themeVariables.strings.searchProducts} (${totalCount})`;
            tabsContainer.style.display = totalCount > 0 ? '' : 'none';
        }

    }

    // Update results count in meta bar (this shows filtered count)
    updateResultsCount(searchResults) {
        if (!this.cachedElements.resultsCount) {
            return;
        }
        
        const count = searchResults.pagination?.totalResults || searchResults.results?.length || 0;
        const t = this.translations;
        
        let countText = `${count} products`;
        
        if (t.productsCount) {
            let template;
            if (count === 0) {
                template = t.productsCount.zero;
            } else if (count === 1) {
                template = t.productsCount.one;
            } else {
                template = t.productsCount.other;
            }
            countText = template.replace(/^\d+/, String(count));
        }
        
        this.cachedElements.resultsCount.textContent = countText;
    }
  
    // Enhanced facets rendering with URL filter support
    renderFacets() {
        const facetsContainer = this.filtersContainer.querySelector('.product-facet__filter-list');
        
        if (!facetsContainer) {
            return;
        }
        
        // Only show the display set: en_ or market prefix, plus globals
        const facetLanguage = window.themeVariables?.settings?.currentLanguage;
        const facetCountryCode = getFacetCountryCodeForRequest();
        const currentMarket = window.themeVariables?.settings?.currentMarket || 'gb';
        const defaultToEnglish = (facetCountryCode !== 'en' && facetLanguage === 'en');
        const displayPrefix = defaultToEnglish ? 'en_' : (facetCountryCode + '_');

        const priceFieldForMarket = SearchSpringCommon.getPriceFieldForMarket(currentMarket);
        const hasDifferentMarketPriceFacet = priceFieldForMarket !== 'ss_price' && this.facetsData.some(f =>
            isPriceFacetField(f.field) && f.field === priceFieldForMarket
        );
        const facetsToRender = this.facetsData.filter(f => {
            if (f.field.endsWith('_included')) return false;
            if (f.field === 'variant_size') return true;
            if (f.field === 'ss_price') return !hasDifferentMarketPriceFacet;
            if (isPriceFacetField(f.field)) return f.field === priceFieldForMarket;
            const marketPrefix = getMarketPrefixFromRef(f.field);
            if (marketPrefix === null) return true; // global (e.g. ss_colour, product_type)
            return marketPrefix === displayPrefix; // market-specific: only show if matches current market
        });
        this.renderCustomFilters(facetsToRender, facetsContainer);
        
        // Update active filters display and count
        this.updateActiveFiltersDisplay();
        
        // Setup functionality
        this.setupFacetCheckboxes();

        this.setupPriceSlider();
    }

    // Renders facets (already market-filtered via includedFacets) with metaobject label overrides
    renderCustomFilters(facetsToRender, facetsContainer) {
        const metaContainer = document.querySelector('[data-ss-meta-filters]');
        const metaFilters = metaContainer ? metaContainer.querySelectorAll(':scope > div[data-reference]') : [];
        const theme = window.themeVariables?.settings || {};
        const facetLanguage = theme.currentLanguage || 'en';
        const facetCountryCode = getFacetCountryCodeForRequest();
        const displayPrefix = (facetCountryCode !== 'en' && facetLanguage === 'en') ? 'en_' : (facetCountryCode + '_');
        const priceRefForMarket = SearchSpringCommon.getPriceFieldForMarket(theme.currentMarket || 'gb');
        const isPriceEnglish = (f) => isPriceFacetField(f.field) && facetLanguage === 'en';

        let html = '';
        facetsToRender.forEach(facet => {
            if (!facet.values?.length && !isPriceFacetField(facet.field)) return;

            const hasActiveValues = (facet.type === 'slider' || isPriceFacetField(facet.field))
                ? (facet.facet_active === 1 || this.currentFilters.has(facet.field))
                : this.currentFilters.has(facet.field);
            const isFilterGroupOpen = hasActiveValues;
            let facetLabel = facet.label;

            if (metaFilters.length) {
                if (isPriceEnglish(facet)) {
                    for (const mf of metaFilters) {
                        const r = mf.getAttribute('data-reference');
                        const l = mf.getAttribute('data-label');
                        if (l && ENGLISH_PRICE_LABEL_REFS.includes(r)) {
                            facetLabel = l;
                            break;
                        }
                    }
                    if (facetLabel === facet.label && (facet.label === facet.field || /^[a-z]{2}_price$/.test(facet.label))) {
                        facetLabel = window.searchSpringTranslations?.priceFilterLabel || 'Price';
                    }
                }

                metaFilters.forEach(metaFilter => {
                    const ref = metaFilter.getAttribute('data-reference');
                    const label = metaFilter.getAttribute('data-label');
                    if (!ref || !label) return;

                    const isGlobalFilter = facet.field === 'variant_size' || isPriceFacetField(facet.field);
                    const marketPrefixedRef = displayPrefix + facet.field;
                    const variantSizeAlias = facet.field === 'variant_size' ? displayPrefix + 'size' : null;

                    if (isGlobalFilter) {
                        if (ref === marketPrefixedRef || ref === variantSizeAlias) facetLabel = label;
                        else if ((ref === 'price' && facet.field === priceRefForMarket) || (ref === facet.field && (ref === 'ss_price' || !isPriceEnglish(facet)))) facetLabel = label;
                        else if ((ref === facet.field || (facet.field === 'variant_size' && ref === 'size')) && (ref === 'ss_price' || !isPriceEnglish(facet))) facetLabel = label;
                    } else if (facet.field === ref) {
                        facetLabel = label;
                    }
                });
            }
            
            if (isPriceFacetField(facet.field)) {
                const sliderHtml = this.renderPriceSliderFacet(facet, isFilterGroupOpen, facetLabel);
                if (sliderHtml) {
                    html += sliderHtml;
                } else {
                    html += `
                    <div class="product-facet__filter-item">
                        <button type="button" is="toggle-button" class="collapsible-toggle text--strong" 
                                aria-controls="facet-filter-${facet.field}" 
                                aria-expanded="${isFilterGroupOpen ? 'true' : 'false'}">
                            ${facetLabel}
                            
                            ${hasActiveValues ? `
                                <span class="collapsible-toggle__selected-value text--subdued text--small hidden-tablet-and-up">
                                    ${this.currentFilters.get(facet.field).join(', ')}
                                </span>
                            ` : ''}
                            
                            <svg focusable="false" width="12" height="8" class="icon icon--chevron icon--inline" viewBox="0 0 12 8">
                                <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.7"></path>
                            </svg>
                        </button>
                        
                        <collapsible-content animate-items ${isFilterGroupOpen ? 'open' : ''} 
                                        id="facet-filter-${facet.field}" class="collapsible">
                            <div class="collapsible__content">
                                ${this.renderFacetValues(facet)}
                            </div>
                        </collapsible-content>
                    </div>
                `;
                }
            } else {
                html += `
                    <div class="product-facet__filter-item">
                        <button type="button" is="toggle-button" class="collapsible-toggle text--strong" 
                                aria-controls="facet-filter-${facet.field}" 
                                aria-expanded="${isFilterGroupOpen ? 'true' : 'false'}">
                            ${facetLabel}
                            
                            ${hasActiveValues ? `
                                <span class="collapsible-toggle__selected-value text--subdued text--small hidden-tablet-and-up">
                                    ${this.currentFilters.get(facet.field).join(', ')}
                                </span>
                            ` : ''}
                            
                            <svg focusable="false" width="12" height="8" class="icon icon--chevron icon--inline" viewBox="0 0 12 8">
                                <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.7"></path>
                            </svg>
                        </button>
                        
                        <collapsible-content animate-items ${isFilterGroupOpen ? 'open' : ''} 
                                        id="facet-filter-${facet.field}" class="collapsible">
                            <div class="collapsible__content">
                                ${this.renderFacetValues(facet)}
                            </div>
                        </collapsible-content>
                    </div>
                `;
            }

        });

        if (facetsContainer && typeof facetsContainer.innerHTML !== 'undefined') {
            facetsContainer.innerHTML = html;
        }

    }

    renderPriceSliderFacet(facet, isFilterGroupOpen, labelOverride) {
        const rate = window.GeShopify?.currency?.rate ?? 1;
        const isGbpBase = facet.field === 'ss_price';
        let rangeMin = 0;
        let rangeMax = 0;
        if (facet.range && facet.range.length >= 2) {
            rangeMin = facet.range[0];
            rangeMax = facet.range[1];
        } else if (facet.type === 'slider' && facet.values && facet.values.length > 0) {
            // SS configured as slider but range in values; derive min/max (e.g. market-specific facets)
            const lows = [];
            const highs = [];
            facet.values.forEach(v => {
                if (v.low != null && v.high != null) {
                    lows.push(Number(v.low));
                    highs.push(Number(v.high));
                } else if (typeof v.value === 'string' && v.value.includes('-')) {
                    const [l, h] = v.value.split('-').map(Number);
                    if (!isNaN(l)) lows.push(l);
                    if (!isNaN(h)) highs.push(h);
                } else if (v.value != null) {
                    const n = Number(v.value);
                    if (!isNaN(n)) {
                        lows.push(n);
                        highs.push(n);
                    }
                }
            });
            if (lows.length && highs.length) {
                rangeMin = Math.min(...lows);
                rangeMax = Math.max(...highs);
            }
        }
        if (rangeMax <= rangeMin) return '';

        const field = facet.field;
        if (!this.initialPriceRangeMap[field]) {
            this.initialPriceRangeMap[field] = { min: Math.floor(rangeMin), max: Math.ceil(rangeMax) };
        }
        const storedRange = this.initialPriceRangeMap?.[field] || this.initialPriceRange;
        const absoluteMaxRangeGbp = storedRange ? storedRange.max : Math.ceil(rangeMax);
        const absoluteMaxRange = isGbpBase ? Math.round(absoluteMaxRangeGbp * rate) : absoluteMaxRangeGbp;

        const maxRange = Math.ceil(rangeMax);

        // Set the default selected range (0 to max). currentFilters/URL store GBP; display in local currency.
        let minValueGbp = 0;
        let maxValueGbp = absoluteMaxRangeGbp;

        if (this.currentFilters.has(field)) {
            const [low, high] = this.currentFilters.get(field)[0].split('-').map(Number);
            minValueGbp = low;
            maxValueGbp = high;
        }

        const minValue = isGbpBase ? Math.round(minValueGbp * rate) : minValueGbp;
        const maxValue = isGbpBase ? Math.round(maxValueGbp * rate) : maxValueGbp;

        // Calculate visual progress bar positions
        const lowerBoundProgress = absoluteMaxRange > 0 ? (minValue / absoluteMaxRange) * 100 : 0;
        const upperBoundProgress = absoluteMaxRange > 0 ? (maxValue / absoluteMaxRange) * 100 : 0;

        const currencySymbol = this.translations?.currency?.symbol || '€';
        const translations = this.translations;

        return `
            <div class="product-facet__filter-item">
                <button type="button" is="toggle-button" class="collapsible-toggle text--strong" 
                        aria-controls="facet-filter-${facet.field}" 
                        aria-expanded="${isFilterGroupOpen ? 'true' : 'false'}">
                    ${labelOverride || facet.label}
                    <svg focusable="false" width="12" height="8" class="icon icon--chevron icon--inline" viewBox="0 0 12 8">
                        <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.7"></path>
                    </svg>
                </button>
                
                <collapsible-content animate-items ${isFilterGroupOpen ? 'open' : ''} 
                                id="facet-filter-${facet.field}" class="collapsible">
                    <div class="collapsible__content">
                        <price-range id="price-slider-${field}" class="price-range" data-price-field="${field}" data-currency-rate="${rate}" data-gbp-base="${isGbpBase}">
                            <div class="price-range__range-group range-group" style="--range-min: ${lowerBoundProgress}%; --range-max: ${upperBoundProgress}%">
                                
                                <input type="range" aria-label="${translations.priceFilterFrom || 'Price filter from'}" class="range" min="0" max="${absoluteMaxRange}" value="${minValue}">
                                <input type="range" aria-label="${translations.priceFilterTo || 'Price filter to'}" class="range" min="0" max="${absoluteMaxRange}" value="${maxValue}">

                            </div>

                            <div class="price-range__input-group">
                                <div class="price-range__input input-prefix text--xsmall">
                                    <span class="input-prefix__value text--subdued" aria-hidden="true">${currencySymbol}</span>
                                    <input aria-label="${translations.priceFilterFrom || 'Price filter from'}" class="input-prefix__field" type="number" 
                                        name="filter.${field}.low" min="0" max="${absoluteMaxRange}" placeholder="0" value="${minValue}">
                                </div>

                                <span class="price-range__delimiter text--small" aria-hidden="true">${translations.priceRangeTo || 'to'}</span>

                                <div class="price-range__input input-prefix text--xsmall">
                                    <span class="input-prefix__value text--subdued" aria-hidden="true">${currencySymbol}</span>
                                    <input aria-label="${translations.priceFilterTo || 'Price filter to'}" class="input-prefix__field" type="number" 
                                        name="filter.${field}.high" min="0" max="${absoluteMaxRange}" placeholder="${absoluteMaxRange}" value="${maxValue}">
                                </div>
                            </div>
                        </price-range>
                    </div>
                </collapsible-content>
            </div>
        `;
    }

    _formatPrice(value) {
        const currency = this.translations?.currency || { symbol: '£', code: 'GBP' };
        // Assuming the price from SearchSpring is already in the correct major unit
        return `${currency.symbol}${parseInt(value)}`;
    }
    
    setupPriceSlider() {
        const sliderContainers = this.filtersContainer.querySelectorAll('price-range[data-price-field]');
        if (!sliderContainers.length) return;
    
        if (this.priceSliderInputHandler) {
            this.filtersContainer.removeEventListener('input', this.priceSliderInputHandler);
        }
        if (this.priceSliderChangeHandler) {
            this.filtersContainer.removeEventListener('change', this.priceSliderChangeHandler);
        }
    
        const debouncersByContainer = new Map();
        const getDebouncedSearch = (sliderContainer) => {
            if (!debouncersByContainer.has(sliderContainer)) {
                debouncersByContainer.set(sliderContainer, this._debounce(() => {
                    const field = sliderContainer.dataset.priceField;
                    const numberInputs = sliderContainer.querySelectorAll('input[type="number"]');
                    const numberMin = numberInputs[0];
                    const numberMax = numberInputs[1];
                    let low = parseInt(numberMin.value) || 0;
                    let high = parseInt(numberMax.value) || parseInt(numberMax.placeholder);
                    if (low >= high) low = high - 1;
                    if (low < 0) low = 0;
                    const rate = parseFloat(sliderContainer.dataset.currencyRate) || 1;
                    const isGbpBase = sliderContainer.dataset.gbpBase === 'true';
                    if (isGbpBase && rate > 0) {
                        low = Math.round(low / rate);
                        high = Math.round(high / rate);
                        if (low >= high) low = Math.max(0, high - 1);
                    }
                    this.currentFilters.set(field, [`${low}-${high}`]);
                    this.searchWithFilters();
                }, 800));
            }
            return debouncersByContainer.get(sliderContainer);
        };
    
        this.priceSliderInputHandler = (e) => {
            const sliderContainer = e.target.closest('price-range[data-price-field]');
            if (!sliderContainer) return;
            const rangeInputs = sliderContainer.querySelectorAll('input[type="range"]');
            const numberInputs = sliderContainer.querySelectorAll('input[type="number"]');
            const rangeMin = rangeInputs[0];
            const rangeMax = rangeInputs[1];
            const numberMin = numberInputs[0];
            const numberMax = numberInputs[1];
            const rangeGroup = sliderContainer.querySelector('.price-range__range-group');
            const target = e.target;
            if (target === rangeMin) {
                let minVal = parseInt(target.value);
                let maxVal = parseInt(rangeMax.value);
                if (minVal >= maxVal) {
                    minVal = maxVal - 1;
                    target.value = minVal;
                }
                numberMin.value = minVal;
                if (rangeGroup && rangeMin.max) {
                    const m = parseInt(rangeMin.max);
                    if (m > 0) {
                        rangeGroup.style.setProperty('--range-min', `${(minVal / m) * 100}%`);
                        rangeGroup.style.setProperty('--range-max', `${(maxVal / m) * 100}%`);
                    }
                }
            } else if (target === rangeMax) {
                let minVal = parseInt(rangeMin.value);
                let maxVal = parseInt(target.value);
                if (maxVal <= minVal) {
                    maxVal = minVal + 1;
                    target.value = maxVal;
                }
                numberMax.value = maxVal;
                if (rangeGroup && rangeMin.max) {
                    const m = parseInt(rangeMin.max);
                    if (m > 0) {
                        rangeGroup.style.setProperty('--range-min', `${(minVal / m) * 100}%`);
                        rangeGroup.style.setProperty('--range-max', `${(maxVal / m) * 100}%`);
                    }
                }
            }
        };
    
        this.priceSliderChangeHandler = (e) => {
            const sliderContainer = e.target.closest('price-range[data-price-field]');
            if (!sliderContainer) return;
            const rangeInputs = sliderContainer.querySelectorAll('input[type="range"]');
            const numberInputs = sliderContainer.querySelectorAll('input[type="number"]');
            const rangeMin = rangeInputs[0];
            const rangeMax = rangeInputs[1];
            const numberMin = numberInputs[0];
            const numberMax = numberInputs[1];
            const rangeGroup = sliderContainer.querySelector('.price-range__range-group');
            const target = e.target;
            const updateVisuals = () => {
                const m = parseInt(rangeMin.max);
                if (rangeGroup && m > 0) {
                    const low = parseInt(numberMin.value);
                    const high = parseInt(numberMax.value);
                    rangeGroup.style.setProperty('--range-min', `${(low / m) * 100}%`);
                    rangeGroup.style.setProperty('--range-max', `${(high / m) * 100}%`);
                }
            };
            if (target === rangeMin || target === rangeMax) {
                getDebouncedSearch(sliderContainer)();
            } else if (target === numberMin) {
                let minVal = parseInt(numberMin.value) || 0;
                let maxVal = parseInt(numberMax.value);
                if (minVal >= maxVal) {
                    minVal = maxVal - 1;
                    target.value = minVal;
                }
                rangeMin.value = minVal;
                updateVisuals();
                getDebouncedSearch(sliderContainer)();
            } else if (target === numberMax) {
                let minVal = parseInt(numberMin.value);
                let maxVal = parseInt(numberMax.value) || parseInt(numberMax.placeholder);
                if (maxVal <= minVal) {
                    maxVal = minVal + 1;
                    target.value = maxVal;
                }
                rangeMax.value = maxVal;
                updateVisuals();
                getDebouncedSearch(sliderContainer)();
            }
        };
    
        this.filtersContainer.addEventListener('input', this.priceSliderInputHandler);
        this.filtersContainer.addEventListener('change', this.priceSliderChangeHandler);
    }

    _debounce(func, delay) {
        let timeout;
        return function(...args) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), delay);
        };
    }

    updateActiveFiltersDisplay() {
        const activeCount = Array.from(this.currentFilters.values()).reduce((sum, values) => sum + values.length, 0);
        
        // Update counter in drawer header
        const counterElement = this.filtersContainer.querySelector('.product-facet__active-count');
        if (counterElement) {
            counterElement.textContent = activeCount;
            counterElement.style.display = activeCount > 0 ? 'inline-flex' : 'none';
        }
        
        // Update counter in meta bar
        const metaBarCounter = document.querySelector('.product-facet__meta-bar .product-facet__active-count');
        if (metaBarCounter) {
            metaBarCounter.textContent = activeCount;
            metaBarCounter.style.display = activeCount > 0 ? 'inline' : 'none';
        }

        // Update the main collection page filter button
        const mainFilterButton = document.querySelector('.product-facet__meta-bar-item--filter .product-facet__active-count');
        if (mainFilterButton) {
            mainFilterButton.textContent = activeCount;
            mainFilterButton.style.display = activeCount > 0 ? 'inline-flex' : 'none';
        }

        // Update mobile filters button
        const mobileFiltersButton = document.querySelector('.mobile-toolbar__item--filters');
        if (mobileFiltersButton) {
            if (activeCount > 0) {
                mobileFiltersButton.classList.add('has-filters');
            } else {
                mobileFiltersButton.classList.remove('has-filters');
            }
        }
        
        // Show/hide clear filters link
        const clearFiltersLink = this.filtersContainer.querySelector('[data-action="clear-all-filters"]');
        if (clearFiltersLink) {
            clearFiltersLink.style.display = activeCount > 0 ? 'block' : 'none';
            clearFiltersLink.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearAllFilters();
            });
        }

        // Render active filter tags
        this.renderActiveFilterTags();
        
        // Update results count
        const resultsCountElement = document.querySelector('.product-facet__meta-bar-item--count');
        if (resultsCountElement && this.lastSearchResults) {
            const count = this.lastSearchResults.pagination?.totalResults || 0;
            const t = this.translations;
            
            let countText = `${count} products`; // fallback
            
            if (t.productsCount) {
                let template;
                if (count === 0) {
                    template = t.productsCount.zero;
                } else if (count === 1) {
                    template = t.productsCount.one;
                } else {
                    template = t.productsCount.other;
                }
                countText = template.replace(/^\d+/, String(count));
            }
            
            resultsCountElement.textContent = countText;
        }
    }

    clearAllFilters() {
        this.currentFilters.clear();

        this.filtersContainer.querySelectorAll('input[type="checkbox"], input[type="radio"]')
            .forEach(input => {
                input.checked = false;
            });

        this.filtersContainer.querySelectorAll('price-range[data-price-field]').forEach(priceRange => {
            this.resetPriceSliderUI(priceRange.dataset.priceField);
        });

        this.searchWithFilters();
    }

    // Resets a price-range slider's inputs/visuals to its full min-max (slider isn't re-rendered on search).
    resetPriceSliderUI(field) {
        const priceRange = this.filtersContainer.querySelector(`price-range[data-price-field="${field}"]`);
        const storedGbp = this.initialPriceRangeMap?.[field] || this.initialPriceRange;
        if (!priceRange || !storedGbp) return;

        const rangeInputs = priceRange.querySelectorAll('input[type="range"]');
        const numberInputs = priceRange.querySelectorAll('input[type="number"]');
        if (!rangeInputs.length || !numberInputs.length) return;

        const rate = parseFloat(priceRange.dataset.currencyRate) || 1;
        const isGbpBase = priceRange.dataset.gbpBase === 'true';
        const max = isGbpBase ? Math.round(storedGbp.max * rate) : storedGbp.max;

        rangeInputs[0].value = 0;
        numberInputs[0].value = 0;
        rangeInputs[1].value = max;
        numberInputs[1].value = max;

        const rangeGroup = priceRange.querySelector('.price-range__range-group');
        if (rangeGroup) {
            rangeGroup.style.setProperty('--range-min', '0%');
            rangeGroup.style.setProperty('--range-max', '100%');
        }
    }

      // Render facet values based on type (color swatches vs regular checkboxes)
    renderFacetValues(facet) {
        const isColorFacet = this.isColorFacet(facet.label);
        const isSizeFacet = this.isSizeFacet(facet.label);
        
        if (isColorFacet) {
          return this.renderColorSwatches(facet);
        } else if (isSizeFacet) {
          return this.renderSizeSwatches(facet);
        } else {
          return this.renderRegularCheckboxes(facet);
        }
    }
      
      // Check if this is a color facet
    isColorFacet(label) {
        const colorLabels = ['color', 'colour', 'couleur', 'kleur', 'farbe', 'cor'];
        return colorLabels.some(colorLabel => 
          label.toLowerCase().includes(colorLabel)
        );
    }
      
      // Check if this is a size facet  
    isSizeFacet(label) {
        const sizeLabels = ['size', 'taille', 'größe', 'dimensione', 'tamanho', 'talla', 'maat'];
        return sizeLabels.some(sizeLabel => 
          label.toLowerCase().includes(sizeLabel)
        );
    }

    getSizeFacetGroup(value) {
        const normalizedValue = String(value || '').trim().toLowerCase();
        const normalizedHandle = normalizedValue.replace(/\s+/g, '-');

        if (
            normalizedHandle === 'onesize' ||
            normalizedHandle.includes('one-size')
        ) {
            return 'oneSize';
        }

        const valueForNumericCheck = this.getSizeNumericCore(normalizedValue);

        const numericRemainder = valueForNumericCheck
            .replace(/[0-9]/g, '')
            .replace(/[.,/\-\s]/g, '');

        if (numericRemainder === '') {
            return 'numerical';
        }

        return 'alphabetical';
    }

    getSizeNumericCore(value) {
        let core = String(value || '').trim().toLowerCase().replace(/^uk\s+/, '');
        if (/\d/.test(core)) {
            core = core.replace(/y$/, '');
        }
        return core;
    }

    getSizeNumericFamily(value) {
        const normalizedValue = String(value || '').trim().toLowerCase();
        if (/^uk\s+/.test(normalizedValue)) {
            return 2;
        }
        if (/\d/.test(normalizedValue) && /y$/.test(normalizedValue)) {
            return 1;
        }
        return 0;
    }

    getSizeNumericSortParts(value) {
        const core = this.getSizeNumericCore(value);
        const nums = core.match(/\d+(?:\.\d+)?/g);
        const start = nums ? parseFloat(nums[0]) : 0;
        const end = nums && nums[1] != null ? parseFloat(nums[1]) : start;
        return { start, end };
    }

    getDefaultClothingSizeOrderTokens() {
        return [
            '4xs', 'xxxxs', 'xxxs', '3xs', 'xxs', '2xs',
            'xs', 's', 'm', 'l', 'xl',
            'xxl', '2xl', 'xxxl', '3xl', 'xxxxl', '4xl', '5xl', '6xl',
            's-m', 'm-l', 'l-xl'
        ];
    }

    normalizeClothingSizeToken(token) {
        return String(token || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/\//g, '-');
    }

    getClothingSizeOrderTokens() {
        if (this._clothingSizeOrderTokens) {
            return this._clothingSizeOrderTokens;
        }

        const raw = window.themeVariables?.settings?.sizeFilterAlphaOrder;
        let tokens = [];

        if (typeof raw === 'string' && raw.trim()) {
            tokens = raw
                .split(/[\n,]+/)
                .map((token) => this.normalizeClothingSizeToken(token))
                .filter(Boolean);
        }

        if (!tokens.length) {
            tokens = this.getDefaultClothingSizeOrderTokens();
        }

        this._clothingSizeOrderTokens = tokens;
        return tokens;
    }

    getClothingSizeRankMap() {
        if (this._clothingSizeRankMap) {
            return this._clothingSizeRankMap;
        }

        const map = Object.create(null);
        this.getClothingSizeOrderTokens().forEach((token, index) => {
            if (map[token] == null) {
                map[token] = index;
            }
        });

        this._clothingSizeRankMap = map;
        return map;
    }

    getClothingSizeRank(token) {
        const key = this.normalizeClothingSizeToken(token);
        const map = this.getClothingSizeRankMap();
        return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;
    }

    getAlphabeticalSizeSortKey(value) {
        const original = String(value || '').trim();
        let core = this.normalizeClothingSizeToken(original);
        let genderRank = 0;

        const fullRank = this.getClothingSizeRank(core);
        if (fullRank != null) {
            return {
                known: 0,
                start: fullRank,
                end: fullRank,
                gender: 0,
                label: original
            };
        }

        const genderSeparated = core.match(/^(.+)-([bg])$/);
        if (genderSeparated) {
            const baseCandidate = genderSeparated[1];
            if (this.getClothingSizeRank(baseCandidate) != null || !baseCandidate.includes('-')) {
                core = baseCandidate;
                genderRank = genderSeparated[2] === 'b' ? 1 : 2;
            }
        } else {
            const genderConcat = core.match(/^(x{0,4}s|x{0,4}l|m|s|l|\d+xl)([bg])$/);
            if (genderConcat) {
                core = genderConcat[1];
                genderRank = genderConcat[2] === 'b' ? 1 : 2;
            }
        }

        let startRank = null;
        let endRank = null;

        if (core.includes('-')) {
            const rangeRank = this.getClothingSizeRank(core);
            if (rangeRank != null) {
                startRank = rangeRank;
                endRank = rangeRank;
            } else {
                const parts = core.split('-').filter(Boolean);
                if (parts.length === 2) {
                    startRank = this.getClothingSizeRank(parts[0]);
                    endRank = this.getClothingSizeRank(parts[1]);
                }
            }
        } else {
            startRank = this.getClothingSizeRank(core);
            endRank = startRank;
        }

        const known = startRank != null;
        return {
            known: known ? 0 : 1,
            start: known ? startRank : 999,
            end: endRank != null ? endRank : (known ? startRank : 999),
            gender: genderRank,
            label: original
        };
    }

    sortSizeFacetValues(values, groupKey) {
        return values.slice().sort((a, b) => {
            if (groupKey === 'numerical') {
                const familyA = this.getSizeNumericFamily(a.value);
                const familyB = this.getSizeNumericFamily(b.value);
                if (familyA !== familyB) {
                    return familyA - familyB;
                }

                const partsA = this.getSizeNumericSortParts(a.value);
                const partsB = this.getSizeNumericSortParts(b.value);
                if (partsA.start !== partsB.start) {
                    return partsA.start - partsB.start;
                }
                if (partsA.end !== partsB.end) {
                    return partsA.end - partsB.end;
                }
            }

            if (groupKey === 'alphabetical') {
                const keyA = this.getAlphabeticalSizeSortKey(a.value);
                const keyB = this.getAlphabeticalSizeSortKey(b.value);
                if (keyA.known !== keyB.known) return keyA.known - keyB.known;
                if (keyA.start !== keyB.start) return keyA.start - keyB.start;
                if (keyA.end !== keyB.end) return keyA.end - keyB.end;
                if (keyA.gender !== keyB.gender) return keyA.gender - keyB.gender;
            }

            return String(a.value).localeCompare(String(b.value), undefined, {
                numeric: true,
                sensitivity: 'base'
            });
        });
    }

      // Render color swatches
    renderColorSwatches(facet) {
        let html = '<div class="color-swatch-list">';

        facet.values.sort((a, b) => {
            // Get the lowercase names
            const nameA = a.value.toLowerCase();
            const nameB = b.value.toLowerCase();
    
            // Find the hex code for each color from our config map
            const hexA = (window.colorSwatchMap[nameA] || nameA).toLowerCase();
            const hexB = (window.colorSwatchMap[nameB] || nameB).toLowerCase();
    
            // Find the canonical English name for each hex code
            const canonicalNameA = window.hexToEnglishColorMap[hexA] || nameA;
            const canonicalNameB = window.hexToEnglishColorMap[hexB] || nameB;
    
            // Compare the canonical English names to sort alphabetically by the English name
            if (canonicalNameA < canonicalNameB) return -1;
            if (canonicalNameA > canonicalNameB) return 1;
            
            // If the canonical names are the same (e.g., both are 'green'),
            // do a secondary sort by the actual display name to keep it consistent.
            return a.value.localeCompare(b.value);
        });
        
        facet.values.forEach((value, index) => {
          const inputId = `${facet.field}-${index}`;
          const isActive = this.currentFilters.has(facet.field) && 
                           this.currentFilters.get(facet.field).includes(value.value);
          const isDisabled = value.count === 0;
          const colorName = value.value.toLowerCase();
          const whiteSynonyms = ['white', 'blanco', 'weiß', 'bianco', 'blanc', 'branco', 'wit'];
          const isWhite = whiteSynonyms.includes(colorName);
          const colorValue = window.colorSwatchMap[colorName] || colorName;

          let ariaLabel = `${value.value}, ${value.count} items`;
          if (isDisabled) {
            ariaLabel += ', out of stock';
          }
          
          html += `
            <div class="color-swatch ${isWhite ? 'color-swatch--white' : ''} ${isDisabled ? 'is-disabled' : ''}" 
                 data-tooltip="${value.value}" data-facet-field="${facet.field}" data-facet-value="${String(value.value).replace(/"/g, '&quot;')}">
              <input class="color-swatch__radio visually-hidden" 
                     type="checkbox" 
                     name="${facet.field}" 
                     id="${inputId}" 
                     value="${value.value}" 
                     title="${value.value}"
                     aria-label="${ariaLabel}"
                     ${isActive ? 'checked="checked"' : ''}
                     ${isDisabled ? 'disabled' : ''}
                     data-facet-field="${facet.field}">
              <label class="color-swatch__item" 
                     for="${inputId}" 
                     style="background-color: ${colorValue}">
                <span class="visually-hidden">${value.value}</span>
              </label>
            </div>
          `;
        });
        
        html += '</div>';
        return html;
    }
      
    renderSizeSwatches(facet) {
        const groupedValues = {
            alphabetical: [],
            numerical: [],
            oneSize: []
        };

        facet.values.forEach(value => {
            groupedValues[this.getSizeFacetGroup(value.value)].push(value);
        });

        let html = '<div class="size-facet-groups">';
        let inputIndex = 0;
        let hasPreviousGroup = false;

        ['alphabetical', 'numerical', 'oneSize'].forEach(groupKey => {
            const values = this.sortSizeFacetValues(groupedValues[groupKey], groupKey);

            if (!values.length) {
                return;
            }

            html += `
                <div class="size-facet-group${hasPreviousGroup ? ' size-facet-group--with-divider' : ''}" data-size-facet-group="${groupKey}">
                    <div class="block-swatch-list block-swatch-list--small">
            `;
            hasPreviousGroup = true;

            values.forEach(value => {
                const inputId = `${facet.field}-${inputIndex}`;
                const isActive = this.currentFilters.has(facet.field) &&
                    this.currentFilters.get(facet.field).includes(value.value);
                const isDisabled = value.count === 0;

                let ariaLabel = `Size ${value.value}, ${value.count} items`;
                if (isDisabled) {
                    ariaLabel += ', out of stock';
                }

                html += `
                    <div class="block-swatch ${isDisabled ? 'is-disabled' : ''}" data-facet-field="${facet.field}" data-facet-value="${String(value.value).replace(/"/g, '&quot;')}">
                      <input class="block-swatch__radio visually-hidden" 
                             type="checkbox" 
                             name="${facet.field}" 
                             id="${inputId}" 
                             value="${value.value}"
                             aria-label="${ariaLabel}"
                             ${isActive ? 'checked="checked"' : ''}
                             ${isDisabled ? 'disabled' : ''}
                             data-facet-field="${facet.field}">
                      <label class="block-swatch__item" for="${inputId}">
                        ${value.value} <span class="facet-count">(${value.count})&lrm;</span>
                      </label>
                    </div>
                `;

                inputIndex += 1;
            });

            html += `
                    </div>
                </div>
            `;
        });

        html += '</div>';
        return html;
    }
      
    // Format facet price like product prices: use theme formatMoney + money format (same as product cards)
    formatFacetPrice(num) {
        if (typeof window.formatMoney !== 'function') return String(num);
        const format = window.themeVariables?.settings?.currencyCodeEnabled
            ? window.themeVariables.settings.moneyWithCurrencyFormat
            : window.themeVariables?.settings?.moneyFormat;
        if (!format) return String(num);
        const cents = Math.round(parseFloat(num) * 100);
        return window.formatMoney(cents, format);
    }

    // Render regular checkboxes
    renderRegularCheckboxes(facet) {
        let html = '';
        const isPriceFacet = facet.field.endsWith('_price');

        facet.values.forEach((value, index) => {
          const inputId = `${facet.field}-${index}`;
          
          // Handle different value structures
          let displayValue, filterValue;
          
          if (value.type === 'range') {
            displayValue = value.label;
            filterValue = `${value.low}-${value.high}`;
          } else {
            displayValue = value.label || value.value || value;
            filterValue = value.value || value;
          }

          // Format price facet values like product prices (e.g. €39,95 for EUR)
          if (isPriceFacet && (typeof filterValue === 'string' || typeof filterValue === 'number')) {
            const num = parseFloat(filterValue);
            if (!Number.isNaN(num)) displayValue = this.formatFacetPrice(num);
          }
          
          const isActive = this.currentFilters.has(facet.field) && 
                           this.currentFilters.get(facet.field).includes(filterValue);
          const isDisabled = value.count === 0;

          let ariaLabel = `${displayValue}, ${value.count} items`;
          if (isDisabled) {
            ariaLabel += ', out of stock';
          }
          
          html += `
            <div class="checkbox-container" data-facet-field="${facet.field}" data-facet-value="${String(filterValue).replace(/"/g, '&quot;')}">
              <input class="checkbox" 
                     type="checkbox" 
                     name="${facet.field}" 
                     id="${inputId}" 
                     value="${filterValue}"
                     aria-label="${ariaLabel}"
                     ${isActive ? 'checked="checked"' : ''}
                     ${isDisabled ? 'disabled' : ''}
                     data-facet-field="${facet.field}">
              <label for="${inputId}">
                ${displayValue} <span class="facet-count" aria-hidden="true" data-facet-field="${facet.field}" data-facet-value="${String(filterValue).replace(/"/g, '&quot;')}">(${value.count})&lrm;</span>
              </label>
            </div>
          `;
        });
        
        return html;
    }
      
    // Update facet counts in place without re-rendering (preserves price slider)
    // When filters are active, API returns only facet values with >0 count; hide the rest.
    // On search page with filters, API may return unfiltered counts; totalResults caps valid values.
    updateFacetCounts(newFacets, totalResults = null) {
        const countMap = new Map();
        const validValuesByField = new Map();
        const hasFilters = this.currentFilters.size > 0;
        const cap = (hasFilters && totalResults != null && totalResults > 0) ? totalResults : null;
        newFacets.forEach(f => {
            if (f.values && !isPriceFacetField(f.field)) {
                const validValues = new Set();
                f.values.forEach(v => {
                    const val = v.value ?? (v.low != null && v.high != null ? `${v.low}-${v.high}` : '');
                    const cnt = v.count ?? 0;
                    if (cap != null && cnt > cap) return;
                    countMap.set(`${f.field}::${val}`, cnt);
                    validValues.add(val);
                });
                validValuesByField.set(f.field, validValues);
            }
        });
        this.filtersContainer.querySelectorAll('[data-facet-field][data-facet-value]').forEach(el => {
            const field = el.getAttribute('data-facet-field');
            const value = el.getAttribute('data-facet-value');
            if (isPriceFacetField(field)) return;
            const validValues = validValuesByField.get(field);
            if (validValues) {
                if (validValues.has(value)) {
                    el.style.display = '';
                    const count = countMap.get(`${field}::${value}`);
                    const countSpan = el.querySelector('.facet-count');
                    if (countSpan && count !== undefined) countSpan.textContent = `(${count})\u200e`;
                } else {
                    el.style.display = 'none';
                }
            } else {
                el.style.display = '';
                const count = countMap.get(`${field}::${value}`);
                if (count !== undefined) {
                    const countSpan = el.querySelector('.facet-count');
                    if (countSpan) countSpan.textContent = `(${count})\u200e`;
                }
            }
        });

        this.updateSizeFacetGroupVisibility();
    }

    updateSizeFacetGroupVisibility() {
        const groups = Array.from(this.filtersContainer.querySelectorAll('.size-facet-group'));
        if (!groups.length) return;

        let hasVisiblePrevious = false;
        groups.forEach(group => {
            const hasVisibleSwatch = Array.from(group.querySelectorAll('.block-swatch')).some(
                swatch => swatch.style.display !== 'none'
            );
            group.style.display = hasVisibleSwatch ? '' : 'none';
            group.classList.toggle('size-facet-group--with-divider', hasVisibleSwatch && hasVisiblePrevious);
            if (hasVisibleSwatch) hasVisiblePrevious = true;
        });
    }

    // Keep existing checkbox setup
    setupFacetCheckboxes() {
        if (this.facetChangeHandler) {
            this.filtersContainer.removeEventListener('change', this.facetChangeHandler);
        }
        
        this.facetChangeHandler = (event) => {
            if (!event.target.matches('[data-facet-field]')) {
                return;
            }
            
            const field = event.target.getAttribute('data-facet-field');
            const value = event.target.value;
            const checked = event.target.checked;
            
            if (checked) {
                this.addFilter(field, value);
            } else {
                this.removeFilter(field, value);
            }
            
            this.searchWithFilters();
        };
        
        this.filtersContainer.addEventListener('change', this.facetChangeHandler);
    }
    
    // Add filter value
    addFilter(field, value) {
      if (!this.currentFilters.has(field)) {
        this.currentFilters.set(field, []);
      }
      if (!this.currentFilters.get(field).includes(value)) {
        this.currentFilters.get(field).push(value);
      }
    }
    
    // Remove filter value
    removeFilter(field, value) {
      if (this.currentFilters.has(field)) {
        const values = this.currentFilters.get(field);
        const index = values.indexOf(value);
        if (index > -1) {
          values.splice(index, 1);
          if (values.length === 0) {
            this.currentFilters.delete(field);
          }
        }
      }
    }
    
    // Search with current filters
    async searchWithFilters(page = 1) {
        this.currentPage = page;
        const filtersObject = Object.fromEntries(this.currentFilters);

        this.renderSkeletonCards(this.productsPerPage);
        
        this.updateUrlWithPagination(page);
        
        try {
            let results;
            if (this.currentQuery) {
                // Search page
                const searchOptions = {
                    page: page,
                    resultsPerPage: this.productsPerPage,
                    filters: Object.keys(filtersObject).length > 0 ? filtersObject : undefined,
                    ...(this.includedFacets?.length ? { includedFacets: this.includedFacets } : {})
                };
    
                // Add sort parameters in SearchSpring format
                if (this.currentSort.field !== 'relevance' && this.currentSort.direction) {
                    searchOptions[`sort.${this.currentSort.field}`] = this.currentSort.direction;
                }
    
                results = await searchProducts(getSiteId(), this.currentQuery, searchOptions);
            } else {
                // Collection page
                const collectionOptions = {
                    collectionHandle: this.collectionHandle,
                    collectionId: this.collectionId,
                    page: page,
                    resultsPerPage: this.productsPerPage,
                    filters: Object.keys(filtersObject).length > 0 ? filtersObject : undefined,
                    ...(this.includedFacets?.length ? { includedFacets: this.includedFacets } : {})
                };
    
                // Add sort parameters in SearchSpring format
                if (this.currentSort.field !== 'relevance' && this.currentSort.direction) {
                    collectionOptions[`sort.${this.currentSort.field}`] = this.currentSort.direction;
                }
    
                results = await searchCollectionProducts(getSiteId(), collectionOptions);
            }
            
            this.lastSearchResults = results;

            if (results.merchandising && results.merchandising.content) {
                renderSearchSpringBanners(results.merchandising.content);
            } else {
                renderSearchSpringBanners(null); // Clear banners if new results have none
            }
    
            
            // Update sort dropdown with new sorting options (in case they changed)
            if (results.sorting) {
                this.renderSortDropdown(results.sorting);
            }
            
            // Update facet counts in place (avoids re-rendering price slider which breaks it)
            if (results.facets && results.facets.length > 0) {
                const totalResults = results.pagination?.totalResults ?? null;
                this.updateFacetCounts(results.facets, totalResults);
            }
            
            await renderSearchSpringProductCards(results, '.search-spring-results-container');
            
            if (results.pagination) {
                this.renderPagination(results.pagination);
            }
            
            this.updateActiveFiltersDisplay();

        } catch (error) {
            console.error('SearchSpring: searchWithFilters failed', error);
        }
    }

    // Update URL with current filters
    updateUrl() {
        // 1. Start with a fresh URLSearchParams object.
        const newParams = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);
    
        // 2. Copy any non-filter parameters from the old URL (like UTM tags, etc.).
        for (const [key, value] of currentParams.entries()) {
            if (!key.startsWith('filter.') && key !== 'page') {
                newParams.append(key, value);
            }
        }
        
        // 3. Add all the currently active filters from our internal state.
        for (const [field, values] of this.currentFilters.entries()) {
            if (isPriceFacetField(field)) {
                values.forEach(value => {
                    if (value.includes('-')) {
                        const [low, high] = value.split('-');
                        newParams.append(`filter.${field}.low`, low);
                        newParams.append(`filter.${field}.high`, high);
                    } else {
                        newParams.append(`filter.${field}`, value);
                    }
                });
            } else {
                // Add all other active filters (like brand, color, etc.)
                values.forEach(value => {
                    newParams.append(`filter.${field}`, value);
                });
            }
        }
    
        // 4. Update the browser URL with our newly built, clean parameter string.
        const newUrl = `${window.location.pathname}?${newParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);
    
    }

    renderActiveFilterTags() {
        const desktopContainer = document.querySelector('.product-facet__active-list.tag-list.hidden-phone');
        const mobileContainer = document.querySelector('.product-facet__active-list.tag-list.hidden-tablet-and-up');
    
        const containers = [desktopContainer, mobileContainer].filter(Boolean);
        if (containers.length === 0) return;
    
        const activeCount = Array.from(this.currentFilters.values()).reduce((sum, values) => sum + values.length, 0);
    
        if (activeCount === 0) {
            // If no filters are active, remove the content and the .is-visible class from both containers
            containers.forEach(container => {
                container.innerHTML = '';
                container.classList.remove('is-visible');
            });
            return;
        }
    
        let tagsHtml = '';
        const translations = this.translations;
        const currency = this.translations?.currency || { symbol: '£', code: 'GBP' };
    
        // Generate individual filter tags
        for (const [field, values] of this.currentFilters.entries()) {
            values.forEach(value => {
                let displayLabel = value;
                let removeUrl = this.generateRemoveFilterUrl(field, value);
    
                if (isPriceFacetField(field)) {
                    if (value.includes('-')) {
                        const [lowGbp, highGbp] = value.split('-').map(Number);
                        const rate = window.GeShopify?.currency?.rate ?? 1;
                        const isGbpBase = field === 'ss_price';
                        const low = isGbpBase && rate > 0 ? Math.round(lowGbp * rate) : lowGbp;
                        const high = isGbpBase && rate > 0 ? Math.round(highGbp * rate) : highGbp;
                        const minFormatted = `${currency.symbol}${low}`;
                        const maxFormatted = `${currency.symbol}${high}`;
                        if (translations.priceFilter) {
                            displayLabel = translations.priceFilter
                                .replace('{{ min_price }}', minFormatted)
                                .replace('{{ max_price }}', maxFormatted);
                        } else {
                            displayLabel = `${minFormatted} - ${maxFormatted}`;
                        }
                    } else {
                        displayLabel = this.formatFacetPrice(parseFloat(value)) || value;
                    }
                }
    
                tagsHtml += `
                    <div class="tag">
                        <a href="#" data-action="remove-filter" data-field="${field}" data-value="${value}" 
                           class="tag__icon tap-area" aria-label="${translations.delete || 'Delete'}">
                            <svg focusable="false" width="10" height="10" class="icon icon--close" viewBox="0 0 14 14">
                                <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="1.7" fill="none"></path>
                            </svg>
                        </a>
                        ${displayLabel}
                    </div>
                `;
            });
        }
    
        if (activeCount > 1) {
            tagsHtml += `
                <a href="#" data-action="clear-all-filters" class="tag-link link text--subdued">
                    ${translations.clearAllFilters}
                </a>
            `;
        }
    
        // Add the content and the .is-visible class to both containers
        containers.forEach(container => {
            container.innerHTML = tagsHtml;
            container.classList.add('is-visible');
        });
    
        this.setupActiveFilterRemoveActions();
    }
    
    // Helper method to generate remove filter URLs (for fallback navigation if JS fails)
    generateRemoveFilterUrl(fieldToRemove, valueToRemove) {
        const newParams = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);
        
        // Copy non-filter parameters
        for (const [key, value] of currentParams.entries()) {
            if (!key.startsWith('filter.') && key !== 'page') {
                newParams.append(key, value);
            }
        }
        
        // Add all filters except the one being removed
        for (const [field, values] of this.currentFilters.entries()) {
            if (field === fieldToRemove) {
                // Only add values that aren't being removed
                const remainingValues = values.filter(v => v !== valueToRemove);
                if (isPriceFacetField(field)) {
                    remainingValues.forEach(value => {
                        if (value.includes('-')) {
                            const [low, high] = value.split('-');
                            newParams.append(`filter.${field}.low`, low);
                            newParams.append(`filter.${field}.high`, high);
                        } else {
                            newParams.append(`filter.${field}`, value);
                        }
                    });
                } else {
                    remainingValues.forEach(value => {
                        newParams.append(`filter.${field}`, value);
                    });
                }
            } else {
                // Add all values for other fields
                if (isPriceFacetField(field)) {
                    values.forEach(value => {
                        if (value.includes('-')) {
                            const [low, high] = value.split('-');
                            newParams.append(`filter.${field}.low`, low);
                            newParams.append(`filter.${field}.high`, high);
                        } else {
                            newParams.append(`filter.${field}`, value);
                        }
                    });
                } else {
                    values.forEach(value => {
                        newParams.append(`filter.${field}`, value);
                    });
                }
            }
        }
        
        return `${window.location.pathname}?${newParams.toString()}`;
    }
    
    // Setup click handlers for active filter removal
    setupActiveFilterRemoveActions() {
        const tagContainers = document.querySelectorAll('.product-facet__active-list.tag-list');
        
        if (tagContainers.length === 0) {
            return;
        }
        
        if (this.filterRemoveHandler) {
            tagContainers.forEach(container => {
                container.removeEventListener('click', this.filterRemoveHandler);
            });
        }
        
        this.filterRemoveHandler = (e) => {
            const removeFilterLink = e.target.closest('[data-action="remove-filter"]');
            if (removeFilterLink) {
                e.preventDefault();
                
                const field = removeFilterLink.dataset.field;
                const value = removeFilterLink.dataset.value;
    
                if (isPriceFacetField(field)) {
                    this.resetPriceSliderUI(field);
                } else {
                    const inputToUncheck = document.querySelector(`input[data-facet-field="${field}"][value="${value}"]`);
                    if (inputToUncheck) {
                        inputToUncheck.checked = false;
                    }
                }
                
                this.removeFilter(field, value);
                this.searchWithFilters();
                return;
            }
            
            const clearAllLink = e.target.closest('[data-action="clear-all-filters"]');
            if (clearAllLink) {
                e.preventDefault();
                this.clearAllFilters();
                return;
            }
        };
        
        tagContainers.forEach(container => {
            container.addEventListener('click', this.filterRemoveHandler);
        });
    }

    renderSortDropdown(sortingData) {
        if (!this.cachedElements.sortContainer || !sortingData?.options) {
            return;
        }

        const translations = this.translations;
        let optionsToRender = [...sortingData.options];

        const isCollectionPage = !window.location.pathname.includes('/search');

        // Add "Featured" option for Collection Pages
        if (isCollectionPage) {
            const relevanceExists = optionsToRender.some(option => option.field === 'relevance');
            const currentLanguage = window.themeVariables?.settings?.currentLanguage || 'en';
            let featuredLabel = translations.sortByFeatured || 'Featured';

            // Force English label if the current language is English
            if (currentLanguage === 'en') {
                 featuredLabel = 'Featured';
            }

            if (!relevanceExists) {
                optionsToRender.unshift({
                    field: 'relevance',
                    label: featuredLabel
                });
            } else {
                const relevanceOption = optionsToRender.find(option => option.field === 'relevance');
                if (relevanceOption && relevanceOption.label !== featuredLabel) {
                    relevanceOption.label = featuredLabel;
                }
            }
        }

        // Filter options for Search page *after* potentially adding Featured for collections
        if (!isCollectionPage) { 
            const allowedSearchSortLabels = [
                'Best Match', 'Relevance', 'Relevanz', 'Relevantie', 'Relevancia',
                'Pertinence', 'In primo piano', 'Pertinenza', 'Price, low to high', 'Price, high to low',
                'Preis, niedrig nach hoch', 'Preis, hoch nach niedrig', 'Precio, menor a mayor',
                'Precio, mayor a menor', 'Prix: faible à élevé', 'Prix: élevé à faible',
                'Prijs: laag naar hoog', 'Prijs: hoog naar laag', 'Preço, mais baratos',
                'Preço, mais caros', 'Prezzo crescente', 'Prezzo decrescente', 'Relevância',
                'Best selling', 'Bestseller', 'Meilleures ventes', 'Más vendidos', 'I più venduti',
                'Best verkocht', 'Mais vendidos', 'Bästsäljare', 'Bestselgere', 'Bestsellery',
                (translations.sortByFeatured || 'Featured')
            ];
             optionsToRender = optionsToRender.filter(option =>
                 allowedSearchSortLabels.includes(option.label)
             );
        }

        // Determine the currently selected option based on internal state (this.currentSort)
        const currentSortField = this.currentSort.field || 'relevance';
        const currentSortDirection = this.currentSort.direction;

        let currentSortOption = optionsToRender.find(option =>
            option.field === currentSortField &&
            (option.direction === currentSortDirection || (option.field === 'relevance' && !currentSortDirection))
        );

        // If no match found (e.g., URL had invalid sort), default to the relevance/featured option
        if (!currentSortOption) {
           currentSortOption = optionsToRender.find(option => option.field === 'relevance') || optionsToRender[0];
           // Update internal state if we defaulted, but only if an option was actually found
           if (currentSortOption) {
                this.currentSort = { field: currentSortOption.field, direction: currentSortOption.direction };
           } else {
               // Handle case where optionsToRender might be empty after filtering
               this.cachedElements.sortContainer.innerHTML = '';
               return;
           }
        }
        const currentSortShopifyValue = this.getSortValue(currentSortOption);
        const currentSortDisplayLabel = (translations.sortByLabels && translations.sortByLabels[currentSortShopifyValue]) || currentSortOption.label;

        // Build HTML using the final optionsToRender list
        const sortHtml = `
            <span class="product-facet__sort-by-title text--subdued hidden-pocket">
                ${translations.sortBy || 'Sort by'}
            </span>
            <div class="popover-container">
                <button type="button" is="toggle-button" class="popover-button hidden-pocket"
                        aria-expanded="false" aria-controls="sort-by-popover" aria-label="${(translations.sortBy || 'Sort by')}: ${currentSortDisplayLabel.replace(/"/g, '&quot;')}">
                    <span id="sort-by-selected-value" aria-hidden="true" style="pointer-events: none">
                        ${currentSortDisplayLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}
                    </span>
                    <svg focusable="false" width="12" height="8" class="icon icon--chevron icon--inline" viewBox="0 0 12 8">
                        <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.7"></path>
                    </svg>
                </button>
                <sort-by-popover id="sort-by-popover" class="popover">
                    <span class="popover__overlay"></span>
                    <header class="popover__header">
                         <span class="popover__title heading h6">${translations.sortBy || 'Sort by'}</span>
                         <button type="button" class="popover__close-button tap-area tap-area--large"
                                 data-action="close" title="${translations.closeAccessibility || 'Close'}">
                             <svg focusable="false" width="16" height="16" class="icon icon--close" viewBox="0 0 16 16">
                                 <path d="M15 1L1 15M1 1l14 14" stroke="currentColor" stroke-width="1.4" fill="none"></path>
                             </svg>
                         </button>
                     </header>
                    <div class="popover__content">
                        <div class="popover__choice-list">
                            ${optionsToRender.map(option => {
                                // Determine if this option is the currently selected one
                                const isSelected = option.field === currentSortField &&
                                                 (option.direction === currentSortDirection || (option.field === 'relevance' && !currentSortDirection));
                                const shopifySortValue = this.getSortValue(option);
                                const displayLabel = (translations.sortByLabels && translations.sortByLabels[shopifySortValue]) || option.label;
                                const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                                return `
                                    <label class="popover__choice-item">
                                        <input type="radio"
                                               data-bind-value="sort-by-selected-value"
                                               class="visually-hidden searchspring-sort-option"
                                               ${isSelected ? 'checked="checked"' : ''}
                                               data-field="${option.field}"
                                               data-direction="${option.direction || ''}"
                                               data-label="${esc(displayLabel)}"
                                               title="${esc(displayLabel)}">
                                        <span class="popover__choice-label">${esc(displayLabel)}</span>
                                    </label>
                                `;
                            }).join('')}
                        </div>
                    </div>
                </sort-by-popover>
            </div>
        `;

        this.cachedElements.sortContainer.innerHTML = sortHtml;
        this.setupSortEventListeners(); // Re-attach listeners
    }
    
    // Helper method to convert SearchSpring sort option to sort value
    getSortValue(option) {
        // Map SearchSpring format to Shopify-compatible format (used for sortByLabels lookup)
        const fieldMappings = {
            'relevance': 'relevance',
            'title': option.direction === 'asc' ? 'title-ascending' : 'title-descending',
            'ss_price': option.direction === 'asc' ? 'price-ascending' : 'price-descending',
            'published_at': option.direction === 'asc' ? 'created-ascending' : 'created-descending',
            'ga_unique_purchases': 'ga_unique_purchases-desc',
            'ga_item_revenue': 'ga_unique_purchases-desc',
            'best_selling': 'ga_unique_purchases-desc',
            'best-selling': 'ga_unique_purchases-desc'
        };
        return fieldMappings[option.field] || `${option.field}-${option.direction}`;
    }
    
    // Convert Shopify sort value back to SearchSpring format
    getSearchSpringSort(shopifySort) {
        const sortMappings = {
            'relevance': { field: 'relevance' },
            'title-ascending': { field: 'title', direction: 'asc' },
            'title-descending': { field: 'title', direction: 'desc' },
            'price-ascending': { field: 'ss_price', direction: 'asc' },
            'price-descending': { field: 'ss_price', direction: 'desc' },
            'created-ascending': { field: 'published_at', direction: 'asc' },
            'created-descending': { field: 'published_at', direction: 'desc' },
            'ga_unique_purchases-desc': { field: 'ga_unique_purchases', direction: 'desc' },
            'ga_item_revenue-desc': { field: 'ga_item_revenue', direction: 'desc' },
            'best-selling': { field: 'ga_unique_purchases', direction: 'desc' },
            'best_selling-desc': { field: 'ga_unique_purchases', direction: 'desc' }
        };
        
        return sortMappings[shopifySort] || 'relevance';
    }
    
    // Setup event listeners for sort options
    setupSortEventListeners() {
        if (!this.cachedElements.sortContainer) {
            return;
        }
        
        if (this.sortChangeHandler) {
            this.cachedElements.sortContainer.removeEventListener('change', this.sortChangeHandler);
        }
        
        this.sortChangeHandler = (e) => {
            if (!e.target.matches('.searchspring-sort-option')) {
                return;
            }
            
            if (e.target.checked) {
                this.currentSort = {
                    field: e.target.dataset.field,
                    direction: e.target.dataset.direction || undefined
                };
                
                const selectedValueSpan = document.getElementById('sort-by-selected-value');
                if (selectedValueSpan) {
                    selectedValueSpan.textContent = e.target.dataset.label;
                }
                
                const popover = document.getElementById('sort-by-popover');
                if (popover) {
                    popover.querySelector('[data-action="close"]').click();
                }
                
                this.updateUrlWithSort();
                this.searchWithFilters();
            }
        };
        
        this.cachedElements.sortContainer.addEventListener('change', this.sortChangeHandler);
    }
    
    // Update URL to include sort parameter
    updateUrlWithSort() {
        const newParams = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);
    
        // Copy non-filter and non-sort parameters
        for (const [key, value] of currentParams.entries()) {
            if (!key.startsWith('filter.') && !key.startsWith('sort.') && key !== 'page') {
                newParams.append(key, value);
            }
        }
        
        // Add sort parameter
        if (this.currentSort.field !== 'relevance' && this.currentSort.direction) {
            newParams.append(`sort.${this.currentSort.field}`, this.currentSort.direction);
        }
        
        // Add current filters
        for (const [field, values] of this.currentFilters.entries()) {
            if (isPriceFacetField(field)) {
                values.forEach(value => {
                    if (value.includes('-')) {
                        const [low, high] = value.split('-');
                        newParams.append(`filter.${field}.low`, low);
                        newParams.append(`filter.${field}.high`, high);
                    } else {
                        newParams.append(`filter.${field}`, value);
                    }
                });
            } else {
                values.forEach(value => {
                    newParams.append(`filter.${field}`, value);
                });
            }
        }
    
        const newUrl = `${window.location.pathname}?${newParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);

    }

    updateUrlWithPagination(page = 1) {
        const newParams = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);
    
        // Copy non-filter, non-sort, non-page parameters
        for (const [key, value] of currentParams.entries()) {
            if (!key.startsWith('filter.') && !key.startsWith('sort.') && key !== 'page') {
                newParams.append(key, value);
            }
        }
        
        // Add page parameter if not page 1
        if (page > 1) {
            newParams.append('page', page);
        }
        
        // Add sort parameter
        if (this.currentSort.field !== 'relevance' && this.currentSort.direction) {
            newParams.append(`sort.${this.currentSort.field}`, this.currentSort.direction);
        }
        
        // Add current filters
        for (const [field, values] of this.currentFilters.entries()) {
            if (isPriceFacetField(field)) {
                values.forEach(value => {
                    if (value.includes('-')) {
                        const [low, high] = value.split('-');
                        newParams.append(`filter.${field}.low`, low);
                        newParams.append(`filter.${field}.high`, high);
                    } else {
                        newParams.append(`filter.${field}`, value);
                    }
                });
            } else {
                values.forEach(value => {
                    newParams.append(`filter.${field}`, value);
                });
            }
        }
    
        const newUrl = `${window.location.pathname}?${newParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);
    
    }

    getSortUrlParam(option) {
        if (option.field === 'relevance') {
            return null; // No sort parameter for relevance
        }
        return { param: `sort.${option.field}`, value: option.direction };
    }
    
    // Convert URL sort parameter back to SearchSpring format for API calls
    getSearchSpringSortFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        
        // Look for any sort.* parameters
        for (const [key, value] of urlParams.entries()) {
            if (key.startsWith('sort.')) {
                const field = key.replace('sort.', '');
                return { field: field, direction: value };
            }
        }
        
        return { field: 'relevance' }; // Default
    }

    renderPagination(paginationData) {
        if (!this.cachedElements.pagination) {
            return;
        }
        
        if (!paginationData || paginationData.totalPages <= 1) {
            this.cachedElements.pagination.style.display = 'none';
            return;
        }
    
        const translations = this.translations;
        const currentPage = this.currentPage || paginationData.currentPage;
        const totalPages = paginationData.totalPages;
        
        // Generate pagination parts (similar to Shopify's paginate.parts)
        const paginationParts = this.generatePaginationParts(currentPage, totalPages);
        
        let paginationHTML = '<nav class="pagination__nav">';
        
        // Previous page link
        if (currentPage > 1) {
            const prevPage = currentPage - 1;
            const prevUrl = this.generatePaginationUrl(prevPage);
            paginationHTML += `
                <a class="pagination__nav-item" 
                   rel="prev" 
                   aria-label="${translations.previousPage || 'Previous page'}" 
                   data-page="${paginationData.previousPage}" 
                   href="${prevUrl}" 
                   data-action="pagination">
                    <svg
                        focusable="false"
                        width="17"
                        height="14"
                        class="icon icon--nav-arrow-left icon--direction-aware"
                        viewBox="0 0 17 14"
                        >
                        <path d="M17 7H2M8 1L2 7l6 6" stroke="currentColor" stroke-width="1.7" fill="none"></path>
                    </svg>
                </a>
            `;
        }
    
        // Page number links
        paginationParts.forEach(part => {
            const pageUrl = this.generatePaginationUrl(part.page);
            if (part.isLink) {
                paginationHTML += `
                    <a href="${pageUrl}" 
                       data-page="${part.page}" 
                       data-action="pagination"
                       class="pagination__nav-item heading heading--small" 
                       aria-label="${translations.goToPage || 'Go to page'} ${part.page}">
                        ${part.page}
                    </a>
                `;
            } else if (part.isEllipsis) {
                paginationHTML += `<span class="pagination__nav-item heading heading--small" aria-hidden="true">…</span>`;
            } else {
                // Current page
                paginationHTML += `
                    <span class="pagination__nav-item heading heading--small" aria-current="page">
                        ${part.page}
                    </span>
                `;
            }
        });
    
        // Next page link  
        if (currentPage < totalPages) {
            const nextPage = currentPage + 1;
            const nextUrl = this.generatePaginationUrl(nextPage);
            paginationHTML += `
                <a class="pagination__nav-item" 
                   rel="next" 
                   aria-label="${translations.nextPage || 'Next page'}" 
                   data-page="${paginationData.nextPage}" 
                   href="${nextUrl}" 
                   data-action="pagination">
                    <svg
                        focusable="false"
                        width="17"
                        height="14"
                        class="icon icon--nav-arrow-right icon--direction-aware"
                        viewBox="0 0 17 14"
                        >
                        <path d="M0 7h15M9 1l6 6-6 6" stroke="currentColor" stroke-width="1.7" fill="none"></path>
                    </svg>
                </a>
            `;
        }
    
        paginationHTML += '</nav>';
    
        this.cachedElements.pagination.innerHTML = paginationHTML;
        this.cachedElements.pagination.style.display = 'flex';
        
        // Setup pagination event listeners
        this.setupPaginationEventListeners();
    }

    generatePaginationParts(currentPage, totalPages) {
        const parts = [];
        
        // Always show first page
        if (currentPage > 3) {
            parts.push({ page: 1, isLink: true });
            if (currentPage > 4) {
                parts.push({ isEllipsis: true });
            }
        }
        
        // Show pages around current page
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, currentPage + 2);
        
        for (let i = start; i <= end; i++) {
            parts.push({ 
                page: i, 
                isLink: i !== currentPage,
                isCurrent: i === currentPage 
            });
        }
        
        // Always show last page
        if (currentPage < totalPages - 2) {
            if (currentPage < totalPages - 3) {
                parts.push({ isEllipsis: true });
            }
            parts.push({ page: totalPages, isLink: true });
        }
        
        return parts;
    }

    setupPaginationEventListeners() {
        if (!this.cachedElements.pagination) {
            return;
        }
        
        if (this.paginationClickHandler) {
            this.cachedElements.pagination.removeEventListener('click', this.paginationClickHandler);
        }
        
        this.paginationClickHandler = (e) => {
            const paginationLink = e.target.closest('[data-action="pagination"]');
            
            if (!paginationLink) {
                return;
            }
            
            e.preventDefault();
            
            const page = parseInt(paginationLink.dataset.page);
            this.currentPage = page;
            this.searchWithFilters(page);
            
            // Scroll to top of results
            const resultsContainer = document.querySelector('#facet-main');
            if (resultsContainer) {
                resultsContainer.scrollIntoView({ behavior: 'smooth' });
            }
        };
        
        this.cachedElements.pagination.addEventListener('click', this.paginationClickHandler);
    }

    generatePaginationUrl(page) {
        const newParams = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);
    
        // Copy non-page parameters
        for (const [key, value] of currentParams.entries()) {
            if (key !== 'page') {
                newParams.append(key, value);
            }
        }
        
        // Add page parameter if not page 1
        if (page > 1) {
            newParams.append('page', page);
        }
        
        const queryString = newParams.toString();
        return `${window.location.pathname}${queryString ? '?' + queryString : ''}`;
    }

    async checkForRedirect(query) {
        if (!query) return;
    
        try {
            const redirectCheck = await searchProducts(getSiteId(), query, { resultsPerPage: 0 });
    
            if (redirectCheck?.merchandising?.redirect) {
                const redirectPath = redirectCheck.merchandising.redirect;
    
                if (redirectPath.startsWith('http')) {
                    window.location.href = redirectPath;
                    return;
                }
    
                const marketPrefixMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})/);
                const marketPrefix = marketPrefixMatch ? marketPrefixMatch[0] : '';
                const finalRedirectUrl = `${marketPrefix}${redirectPath}`;
                
                window.location.href = finalRedirectUrl;
                return;
            }
        } 
        catch (err) {
            console.error('Searchspring Redirect Error: ', err)
        }
    }

    renderSkeletonCards(count) {
        const resultsContainer = document.querySelector('.search-spring-results-container');
        if (!resultsContainer) return;

        const skeletonHTML = `
            <div class="product-item">
                <div class="product-item__image-wrapper">
                    <div class="skeleton-loader" style="padding-bottom: 142%;"></div>
                </div>
                <div class="product-item__info">
                    <div class="product-item-meta" style="padding-top: 1rem;">
                        <div class="skeleton-loader" style="height: 1em; width: 60%; margin: 0 auto 0.5rem;"></div>
                        <div class="skeleton-loader" style="height: 1.5em; width: 90%; margin: 0 auto 0.75rem;"></div>
                        <div class="skeleton-loader" style="height: 1em; width: 40%; margin: 0 auto;"></div>
                    </div>
                </div>
            </div>
        `;
        
        resultsContainer.innerHTML = Array(count).fill(skeletonHTML).join('');
    }

    // ─────────────────────────────────────────────────────────────────────
    // PLP/search scroll & page restoration on back/forward navigation
    // ─────────────────────────────────────────────────────────────────────
    
    getScrollStorageKey() {
        return `ss-plp-scroll:${window.location.pathname}${window.location.search}`;
    }

    isBackForwardNavigation() {
        try {
            const [entry] = performance.getEntriesByType('navigation');
            return entry ? entry.type === 'back_forward' : false;
        } catch (e) {
            console.error('SearchSpring: isBackForwardNavigation failed', e);
            return false;
        }
    }

    saveScrollPosition() {
        try {
            sessionStorage.setItem(this.getScrollStorageKey(), String(window.scrollY));
        } catch (e) {
            console.error('SearchSpring: saveScrollPosition failed', e);
        }
    }

    setupScrollPersistence() {
        if (window.searchSpringScrollPersistenceSetup) return;
        window.searchSpringScrollPersistenceSetup = true;

        window.addEventListener('pagehide', () => this.saveScrollPosition());
    }

    restoreScrollPositionIfNeeded() {
        if (!this.isBackForwardNavigation()) return;

        let savedY;
        try {
            const raw = sessionStorage.getItem(this.getScrollStorageKey());
            if (raw === null) return;
            savedY = parseInt(raw, 10);
        } catch (e) {
            console.error('SearchSpring: restoreScrollPositionIfNeeded failed', e);
            return;
        }

        if (!Number.isFinite(savedY)) return;

        const scrollToSaved = () => window.scrollTo(0, savedY);

        requestAnimationFrame(() => requestAnimationFrame(scrollToSaved));
        setTimeout(scrollToSaved, 250);
        setTimeout(scrollToSaved, 600);
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// SearchSpring Collection Facets - extends SearchSpringFacets for collection pages
// ─────────────────────────────────────────────────────────────────────────────

class SearchSpringCollectionFacets extends SearchSpringFacets {
    constructor(collectionHandle, collectionId = null) {
        super();
        this.collectionHandle = collectionHandle;
        this.collectionId = collectionId;
        this.currentQuery = '';
        this.currentPage = 1;
        this.initialPriceRange = null;
        this.initialPriceRangeMap = {};
        this.productsPerPage = getSectionProductsPerPage();
        this.preloadImageCount = window.innerWidth >= 1200 ? 4 : 2;
    }

    // Override init for collection pages
    async init() {
        this.setupScrollPersistence();

        this.addPreloadImages();
        this.filtersContainer = document.getElementsByClassName('searchspring-facet-filters')[0];
        if (!this.filtersContainer) {
            return;
        }

        this.renderSkeletonCards(this.productsPerPage);

        this.parseUrlFilters();

        this.includedFacets = getIncludedFacetsFromMetaFilters();

        if (this.currentFilters.size > 0) {
            // Perform a pre-flight request with NO filters...
            const preflightOptions = {
                collectionHandle: this.collectionHandle,
                collectionId: this.collectionId,
                resultsPerPage: 0 // We don't need products, just the facets.
            };
            if (this.includedFacets?.length) preflightOptions.includedFacets = this.includedFacets;
            const preflightResults = await searchCollectionProducts(getSiteId(), preflightOptions);
    
            // ...just to get the full facet data and store the initial price range for each price facet.
            if (preflightResults.facets) {
                preflightResults.facets.filter(f => isPriceFacetField(f.field) && f.range?.length >= 2).forEach(priceFacet => {
                    this.initialPriceRangeMap[priceFacet.field] = {
                        min: Math.floor(priceFacet.range[0]),
                        max: Math.ceil(priceFacet.range[1])
                    };
                    if (priceFacet.field === 'ss_price') this.initialPriceRange = this.initialPriceRangeMap[priceFacet.field];
                });
            }
        }
      
        this.setupFiltersStructure();
        await this.loadCollectionWithFacets();
    }

    // Override setupFiltersStructure for collections (no search query input)
    setupFiltersStructure() {
        const isDrawer = this.filtersContainer.classList.contains('drawer');
        const translations = this.translations;
        
        this.filtersContainer.innerHTML = `
            <span class="drawer__overlay"></span>
            
            ${isDrawer ? `
                <header class="drawer__header">
                    <p class="drawer__title heading h6">${translations.filters}
                        <span class="product-facet__active-count bubble-count" style="display: none;">0</span>
                    </p>
                    <a class="drawer__header-action link text--subdued" style="display: none;" data-action="clear-all-filters" href="#">${translations.clearFilters}</a>
                    <button type="button" class="drawer__close-button tap-area" data-action="close" title="${translations.closeAccessibility}">
                        <svg focusable="false" width="16" height="16" class="icon icon--close" viewBox="0 0 16 16">
                            <path d="M15 1L1 15M1 1l14 14" stroke="currentColor" stroke-width="1.4" fill="none"></path>
                        </svg>
                    </button>
                </header>
            ` : ''}
            
            <div class="drawer__content">
                <form id="searchspring-facet-filters-form">
                    <input type="hidden" name="sort_by" value="${this.currentSort}">
                    <input type="hidden" name="type" value="product">
                    
                    <div class="product-facet__active-list tag-list hidden-phone"></div>
                    <div class="product-facet__filter-list">
                        <p>${translations.loadingFilters}</p>
                    </div>
                </form>
            </div>
            
            ${isDrawer ? `
                <div class="drawer__footer drawer__footer--no-top-padding">
                    <button type="button" class="button button--primary button--full" data-action="close">${translations.viewResults}</button>
                </div>
            ` : ''}
        `;
    }

    // Load collection results with facets
    async loadCollectionWithFacets() {
        try {
            const filtersObject = Object.fromEntries(this.currentFilters);
            const hasUrlFilters = this.currentFilters.size > 0 || (this.currentSort.field !== 'relevance');
            // When URL has filters/sort, include them in initial fetch to avoid showing unfiltered count (37) before filtered count (5)
            const initialSearchOptions = {
                collectionHandle: this.collectionHandle,
                collectionId: this.collectionId,
                page: this.currentPage,
                resultsPerPage: this.productsPerPage,
                filters: hasUrlFilters && Object.keys(filtersObject).length > 0 ? filtersObject : undefined,
                ...(this.includedFacets?.length ? { includedFacets: this.includedFacets } : {})
            };
            if (hasUrlFilters && this.currentSort.field !== 'relevance' && this.currentSort.direction) {
                initialSearchOptions[`sort.${this.currentSort.field}`] = this.currentSort.direction;
            }
            const initialResults = await searchCollectionProducts(getSiteId(), initialSearchOptions);
            // --- End Initial Call Modification ---

            this.lastSearchResults = initialResults; // Store initial results
            this.facetsData = initialResults.facets || [];

            if (initialResults.merchandising && initialResults.merchandising.content) {
                renderSearchSpringBanners(initialResults.merchandising.content);
            }

            // Store initial price range for each price facet from these initial results
            if (this.facetsData && this.facetsData.length > 0) {
                this.facetsData.filter(f => isPriceFacetField(f.field) && f.range?.length >= 2).forEach(priceFacet => {
                    if (!this.initialPriceRangeMap[priceFacet.field]) {
                        this.initialPriceRangeMap[priceFacet.field] = {
                            min: Math.floor(priceFacet.range[0]),
                            max: Math.ceil(priceFacet.range[1])
                        };
                        if (priceFacet.field === 'ss_price') this.initialPriceRange = this.initialPriceRangeMap[priceFacet.field];
                    }
                });
            }

            // Render initial facets based on the first load
            if (this.facetsData.length > 0) {
                 this.renderFacets();
            }

            this.cacheElements(); // Cache elements

            // Render sort dropdown using options from initialResults
            if (initialResults.sorting) {
                // This will now include the "Featured" option by default
                this.renderSortDropdown(initialResults.sorting);
            }

            // Initial fetch now includes URL filters/sort when present, so we always render initialResults
            await renderSearchSpringProductCards(initialResults, '.search-spring-results-container');
            if (initialResults.pagination) {
                this.renderPagination(initialResults.pagination);
            }
            this.updateActiveFiltersDisplay();

            this.restoreScrollPositionIfNeeded();

        } catch (err) {
            console.error('SearchSpring: loadCollectionWithFacets failed', err);
        }
    }

    async searchWithFilters(page = 1) {
        this.addPreloadImages(page);
        this.currentPage = page;
        const filtersObject = Object.fromEntries(this.currentFilters);

        this.updateUrlWithPagination(page);
        
        try {
            const collectionOptions = {
                collectionHandle: this.collectionHandle,
                collectionId: this.collectionId,
                page: page,
                resultsPerPage: this.productsPerPage,
                filters: Object.keys(filtersObject).length > 0 ? filtersObject : undefined,
                ...(this.includedFacets?.length ? { includedFacets: this.includedFacets } : {})
            };

            // Add sort parameters in SearchSpring format
            if (this.currentSort.field !== 'relevance' && this.currentSort.direction) {
                collectionOptions[`sort.${this.currentSort.field}`] = this.currentSort.direction;
            }

            const results = await searchCollectionProducts(getSiteId(), collectionOptions);
        
            this.lastSearchResults = results;

            if (results.merchandising && results.merchandising.content) {
                renderSearchSpringBanners(results.merchandising.content);
            } else {
                renderSearchSpringBanners(null); // Clear banners if new results have none
            }

            if (results.pagination) {
                this.renderPagination(results.pagination);
            }
            
            if (results.sorting) {
                this.renderSortDropdown(results.sorting);
            }
            
            // Update facet counts in place (avoids re-rendering price slider which breaks it)
            if (results.facets && results.facets.length > 0) {
                const totalResults = results.pagination?.totalResults ?? null;
                this.updateFacetCounts(results.facets, totalResults);
            }
        
            await renderSearchSpringProductCards(results, '.search-spring-results-container');
            this.updateActiveFiltersDisplay();

        } catch (error) {
            console.error('SearchSpring: searchWithFilters failed', error);
        }
    }

    clearAllFilters() {
        super.clearAllFilters();
    }

    async addPreloadImages(page = 1) {
        const filtersObject = Object.fromEntries(this.currentFilters);
        try {
            const preloadOptions = {
                collectionHandle: this.collectionHandle,
                collectionId: this.collectionId,
                page: page,
                resultsPerPage: this.preloadImageCount,
                filters: Object.keys(filtersObject).length > 0 ? filtersObject : undefined,
                ...(this.includedFacets?.length ? { includedFacets: this.includedFacets } : {}),
                beacon: false
            };

            if (this.currentSort.field !== 'relevance' && this.currentSort.direction) {
                preloadOptions[`sort.${this.currentSort.field}`] = this.currentSort.direction;
            }

            const results = await searchCollectionProducts(getSiteId(), preloadOptions);
            if(results && results.results) {
                buildPreloadLinks(results.results);
            }
        } catch (err) {
            console.error('SearchSpring: addPreloadImages failed', err);
        }
    }

    updateUrl() {
        const newParams = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);

        for (const [key, value] of currentParams.entries()) {
            if (!key.startsWith('filter.') && key !== 'page') {
                newParams.append(key, value);
            }
        }
        
        // 3. Add all the currently active filters from our internal state.
        for (const [field, values] of this.currentFilters.entries()) {
            if (isPriceFacetField(field)) {
                values.forEach(value => {
                    if (value.includes('-')) {
                        const [low, high] = value.split('-');
                        newParams.append(`filter.${field}.low`, low);
                        newParams.append(`filter.${field}.high`, high);
                    } else {
                        newParams.append(`filter.${field}`, value);
                    }
                });
            } else {
                values.forEach(value => {
                    newParams.append(`filter.${field}`, value);
                });
            }
        }

        const newUrl = `${window.location.pathname}?${newParams.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);
    }
}

// Make the new class available globally
window.SearchSpringCollectionFacets = SearchSpringCollectionFacets;

if (window.location.pathname.includes('/search')) {
    window.searchSpringFacets = new SearchSpringFacets();
    window.searchSpringFacets.init();
}
